'use client';
import FlowSimulator from './FlowSimulator';
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { downloadJsonFile, uploadWhatsAppFlowMedia } from '../api';
import { getWindexStatus } from '../../api';
import { stripImportedIds } from '../utils/canvasImport';
import { parseFlowDocument, serializeCanvasFlow } from '../utils/flowJson';
import { TimePickerInput } from '../../appointments/components/TimePickerInput';
import {
  Button,
  CustomSelect,
  SideDrawer,
  ConfirmDeleteModal,
  Switch,
} from '@/components';
import { useUnsavedChangesGuard, useFeatures } from '@/common/hooks';
import {
  ReactFlow,
  useReactFlow,
  Controls,
  Background,
  MarkerType,
  ConnectionLineType,
  Handle,
  Position,
  applyNodeChanges,
  applyEdgeChanges,
  useConnection,
  useUpdateNodeInternals,
  EdgeLabelRenderer,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { PlusIcon, CloseIcon, DuplicateIcon, UploadIcon } from './icons';
import { CanvasTagChips, InternalTagPicker } from './InternalTagPicker';
import { ImportFlowModal } from './ImportFlowModal';
import { SearchNodeModal } from './SearchNodeModal';
import {
  applyRemoteStepTags,
  hydrateAutomationTags,
  serializeStepTags,
} from '../utils/tags';
import { KeywordTagInput } from './KeywordTagInput';
import { QuotationPicker } from './QuotationPicker';
import { GoogleSheetStepFields } from './GoogleSheetStepFields';
import { WindexStepFields } from './WindexStepFields';
import { Modal } from '@/components';
import { PipelineStageSelect } from './PipelineStageSelect';
import { getCRMPipelineStages } from '../../crm/api';
import { resolveStageId, withStageId } from '../utils/pipelineStage';
import {
  googleSheetFields,
  isGoogleSheetStep,
  sanitizeVariableName,
  SHEET_FOUND_HANDLE,
  SHEET_NOT_FOUND_HANDLE,
  SHEET_NO_MATCH_DEFAULT,
  sheetFoundNextOrder,
  sheetNotFoundNextOrder,
  validateGoogleSheetStep,
} from '../utils/googleSheetStep';
import {
  isWindexStep,
  validateWindexStep,
  WINDEX_FOUND_HANDLE,
  WINDEX_NO_RESULT_HANDLE,
  windexFields,
  windexNoResultNextOrder,
} from '../utils/windexStep';
import { parseKeywords } from '../utils/keywords';
import trashIcon from '../../assets/trashIcon.png';
import triggerIcon from '../../assets/Sidebar/trigger.png';
import windexIcon from '../../assets/Windex_icon.jpg';

const APPOINTMENT_TIMEZONE = 'Asia/Kolkata';
const SLOT_DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];
const AVAILABLE_DAYS_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

const stripMarkdown = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/(~~|~)(.*?)\1/g, '$2')
    .replace(/^#+\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
};

const toTimeInputValue = (value, fallback = '09:00') => {
  if (!value || typeof value !== 'string') return fallback;
  return value.slice(0, 5);
};

const toApiTimeValue = (value) => {
  if (!value) return null;
  return value.length === 5 ? `${value}:00` : value;
};

const timeToMinutes = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [hh, mm] = value.slice(0, 5).split(':').map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const validateDateTimeStep = (step) => {
  const workStart = timeToMinutes(step.working_hours_start);
  const workEnd = timeToMinutes(step.working_hours_end);
  if (workStart == null || workEnd == null || workStart >= workEnd) {
    return {
      ok: false,
      error: `Step ${step.order}: Opens at must be before Closes at.`,
    };
  }

  const duration = Number(step.slot_duration_minutes);
  if (!duration || duration <= 0) {
    return {
      ok: false,
      error: `Step ${step.order}: Slot duration must be greater than 0.`,
    };
  }

  const days = Number(step.available_days_count);
  if (!days || days < 1 || days > 10) {
    return {
      ok: false,
      error: `Step ${step.order}: Available days must be between 1 and 10.`,
    };
  }

  const hasBreakStart = !!step.break_time_start;
  const hasBreakEnd = !!step.break_time_end;
  if (hasBreakStart !== hasBreakEnd) {
    return {
      ok: false,
      error: `Step ${step.order}: Break start and end must both be set, or both cleared.`,
    };
  }

  if (hasBreakStart && hasBreakEnd) {
    const breakStart = timeToMinutes(step.break_time_start);
    const breakEnd = timeToMinutes(step.break_time_end);
    if (
      breakStart == null ||
      breakEnd == null ||
      breakStart >= breakEnd ||
      breakStart <= workStart ||
      breakEnd >= workEnd
    ) {
      return {
        ok: false,
        error: `Step ${step.order}: Break must sit strictly inside working hours.`,
      };
    }
  }

  return { ok: true };
};

const resolveQuotationId = (step) => {
  if (step?.quotation_id != null && step.quotation_id !== '') {
    return Number(step.quotation_id);
  }
  if (step?.quotation != null && typeof step.quotation === 'object') {
    return step.quotation.id != null ? Number(step.quotation.id) : null;
  }
  if (step?.quotation != null && step.quotation !== '') {
    return Number(step.quotation);
  }
  return null;
};

const cleanQuotationStep = (step) => ({
  ...step,
  options: [],
  is_quotation_step: true,
  is_open_ended: false,
  is_confirmation_step: false,
  is_datetime_step: false,
  quotation_id: resolveQuotationId(step),
  quotation_show_price:
    step.quotation_show_price === true || step.quotation_show_price === false
      ? step.quotation_show_price
      : null,
});

const cleanDateTimeStep = (step) => {
  const cleaned = {
    ...step,
    options: [],
    is_open_ended: false,
    is_confirmation_step: false,
    is_datetime_step: true,
    is_quotation_step: false,
    timezone: APPOINTMENT_TIMEZONE,
    working_hours_start:
      toApiTimeValue(toTimeInputValue(step.working_hours_start, '09:00')) ||
      '09:00:00',
    working_hours_end:
      toApiTimeValue(toTimeInputValue(step.working_hours_end, '17:00')) ||
      '17:00:00',
    slot_duration_minutes: Number(step.slot_duration_minutes) || 30,
    available_days_count: Math.min(
      10,
      Math.max(1, Number(step.available_days_count) || 7),
    ),
  };

  if (step.break_time_start && step.break_time_end) {
    cleaned.break_time_start = toApiTimeValue(
      toTimeInputValue(step.break_time_start, '13:00'),
    );
    cleaned.break_time_end = toApiTimeValue(
      toTimeInputValue(step.break_time_end, '14:00'),
    );
  } else {
    delete cleaned.break_time_start;
    delete cleaned.break_time_end;
  }

  return cleaned;
};

const validateQuotationStep = (step) => {
  const quotationId = resolveQuotationId(step);
  if (!quotationId) {
    return {
      ok: false,
      error: `Step ${step.order}: Please select a product catalog for this quotation step.`,
    };
  }
  return { ok: true };
};

/** Soft limit for option button text UI counter (does not block save/publish). */
const OPTION_TEXT_MAX_LENGTH = 24;

const cleanFlowSteps = (steps) => {
  const seenStepIds = new Set();
  return steps.map((step) => {
    let sanitizedStep = withStageId(step);
    delete sanitizedStep.google_sheet_file_name;
    sanitizedStep.options = (sanitizedStep.options || []).map((opt) =>
      withStageId(opt),
    );
    if (sanitizedStep.id != null) {
      const idKey = String(sanitizedStep.id);
      if (seenStepIds.has(idKey)) {
        delete sanitizedStep.id;
      } else {
        seenStepIds.add(idKey);
      }
    }

    if (sanitizedStep.is_quotation_step) {
      return serializeStepTags({
        ...cleanQuotationStep(sanitizedStep),
        ...googleSheetFields({ is_google_sheet_step: false }),
        ...windexFields({ is_windex_step: false }),
      });
    }
    if (sanitizedStep.is_datetime_step) {
      return serializeStepTags({
        ...cleanDateTimeStep(sanitizedStep),
        ...googleSheetFields({ is_google_sheet_step: false }),
        ...windexFields({ is_windex_step: false }),
      });
    }
    if (isWindexStep(sanitizedStep)) {
      const fields = windexFields(sanitizedStep);
      const payload = {
        ...sanitizedStep,
        options: [],
        is_open_ended: false,
        is_confirmation_step: false,
        is_datetime_step: false,
        is_quotation_step: false,
        quotation_id: null,
        ...googleSheetFields({ is_google_sheet_step: false }),
        ...fields,
      };
      if (!fields.windex_field) delete payload.windex_field;
      return serializeStepTags(payload);
    }
    if (isGoogleSheetStep(sanitizedStep)) {
      return serializeStepTags({
        ...sanitizedStep,
        options: [],
        is_open_ended: false,
        is_confirmation_step: false,
        is_datetime_step: false,
        is_quotation_step: false,
        quotation_id: null,
        ...googleSheetFields(sanitizedStep),
        ...windexFields({ is_windex_step: false }),
      });
    }
    const seenOptIds = new Set();
    const cleanedOptions = (sanitizedStep.options || [])
      .filter((opt) => opt.text && opt.text.trim() !== '')
      .map((opt) => {
        if (opt.id != null) {
          const optKey = String(opt.id);
          if (seenOptIds.has(optKey)) {
            const restOpt = { ...opt };
            delete restOpt.id;
            return restOpt;
          }
          seenOptIds.add(optKey);
        }
        return opt;
      });
    return serializeStepTags({
      ...sanitizedStep,
      is_quotation_step: false,
      quotation_id: null,
      options: cleanedOptions,
      variable_name: sanitizedStep.is_open_ended
        ? sanitizeVariableName(sanitizedStep.variable_name)
        : '',
      ...googleSheetFields({ is_google_sheet_step: false }),
      ...windexFields({ is_windex_step: false }),
    });
  });
};

// Node heights vary a lot (a List Options card with several options is far
// taller than a plain message card) — using each node's real measured size
// (falling back to the 320x100 default only when unmeasured) keeps rows
// spaced to their actual content instead of overlapping/crossing.
const DagreLayout = (nodes, edges, direction = 'LR') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  const isHorizontal = direction === 'LR';

  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: 120,
    nodesep: 90,
  });

  const sizeOf = (node) => {
    const stepTags = node.data?.fullStep?.tags?.length ? 24 : 0;
    const optionCount = node.data?.fullStep?.options?.length || 0;
    const optionTagExtra = (node.data?.fullStep?.options || []).reduce(
      (sum, opt) => sum + (opt.tags?.length ? 18 : 0),
      0,
    );
    // Accurate card height: Header (~96px) + Options (~48px each) + tags + margins
    const calculatedH = node.data?.hasOptions
      ? 100 + stepTags + optionCount * 48 + optionTagExtra
      : 110 + stepTags;
    return {
      width: node.measured?.width || 344,
      height: Math.max(node.measured?.height || 0, calculatedH),
    };
  };

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, sizeOf(node));
  });

  edges.forEach((edge) => {
    const labelText = typeof edge.label === 'string' ? edge.label : '';
    const labelWidth = labelText
      ? Math.max(100, labelText.length * 7.5 + 36)
      : 0;
    dagreGraph.setEdge(edge.source, edge.target, {
      width: labelWidth,
      height: edge.label ? 28 : 0,
    });
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const { width, height } = sizeOf(node);
    node.targetPosition = isHorizontal ? 'left' : 'top';
    node.sourcePosition = isHorizontal ? 'right' : 'bottom';

    node.position = {
      x: nodeWithPosition.x - width / 2,
      y: nodeWithPosition.y - height / 2,
    };
    return node;
  });

  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  // Group nodes by topological column X (ordered from left to right)
  const nodesByColumn = new Map();
  nodes.forEach((n) => {
    const colKey = Math.round(n.position.x / 50);
    if (!nodesByColumn.has(colKey)) {
      nodesByColumn.set(colKey, []);
    }
    nodesByColumn.get(colKey).push(n);
  });

  // Sort columns strictly from left to right
  const sortedColKeys = Array.from(nodesByColumn.keys()).sort((a, b) => a - b);

  // Compute adaptive X position for each column:
  // If a column only has 1 node or simple straight edges, keep it compact (~140px-240px);
  // If a column has multiple stacked nodes with options (1 1 2 2...), allocate dedicated channel width!
  let currentColumnX = 80;
  sortedColKeys.forEach((colKey) => {
    const colNodes = nodesByColumn.get(colKey);
    colNodes.forEach((n) => {
      n.position.x = currentColumnX;
    });

    const maxOptionsCount = colNodes.reduce(
      (max, n) => Math.max(max, (n.data?.fullStep?.options || []).length || 0),
      0,
    );

    const colNodesCount = colNodes.length;
    const laneBundles = Math.max(1, colNodesCount);
    const lanesPerBundle = Math.max(1, maxOptionsCount);
    const laneSpan =
      lanesPerBundle * laneBundles > 1
        ? (lanesPerBundle * laneBundles - 1) * 18
        : 0;
    const longestLabel = colNodes.reduce((max, n) => {
      const opts = n.data?.fullStep?.options || [];
      return opts.reduce(
        (m, opt) => Math.max(m, String(opt.text || '').length),
        max,
      );
    }, 0);
    const labelLane = Math.min(240, longestLabel * 7.2 + 28);
    const colGap = Math.max(128, 48 + labelLane + 40 + laneSpan);
    const CARD_WIDTH = 344;
    currentColumnX += CARD_WIDTH + colGap;
  });

  // Generous gap between vertically stacked cards so lines and cards never overlap
  const UNIFORM_GAP = 96;

  nodesByColumn.forEach((colNodes) => {
    if (colNodes.length < 1) return;

    colNodes.forEach((node) => {
      let sumY = 0;
      let count = 0;

      const targetHandleOffsetY = node.data?.hasOptions
        ? 34
        : sizeOf(node).height / 2;

      edges.forEach((edge) => {
        if (edge.target === node.id) {
          const sourceNode = nodeById.get(edge.source);
          if (sourceNode?.data?.hasOptions) {
            const opts = sourceNode.data?.fullStep?.options || [];
            const optNum = parseInt(
              (edge.sourceHandle || '').replace('opt_', ''),
              10,
            );
            const optIdx = opts.findIndex((o) => o.number === optNum);
            if (optIdx >= 0) {
              const optYOffset = 100 + optIdx * 44;
              const sourceHandleY = sourceNode.position.y + optYOffset;
              sumY += sourceHandleY - targetHandleOffsetY;
              count++;
            }
          } else if (sourceNode) {
            const sourceHandleY =
              sourceNode.position.y + sizeOf(sourceNode).height / 2;
            sumY += sourceHandleY - targetHandleOffsetY;
            count++;
          }
        }
      });

      node._desiredY = count > 0 ? sumY / count : node.position.y;
    });

    if (colNodes.length === 1) {
      colNodes[0].position.y = colNodes[0]._desiredY;
      delete colNodes[0]._desiredY;
      return;
    }

    // Sort column nodes from top to bottom by their desired Y
    colNodes.sort((a, b) => a._desiredY - b._desiredY);

    const N = colNodes.length;
    const yPos = colNodes.map((n) => n._desiredY);

    // Forward pass: resolve collisions downwards
    for (let i = 1; i < N; i++) {
      const prevBottom =
        yPos[i - 1] + sizeOf(colNodes[i - 1]).height + UNIFORM_GAP;
      if (yPos[i] < prevBottom) {
        yPos[i] = prevBottom;
      }
    }

    // Center the group around the desired centroid
    const avgDesired = colNodes.reduce((sum, n) => sum + n._desiredY, 0) / N;
    const avgCurrent = yPos.reduce((sum, y) => sum + y, 0) / N;
    const shift = avgDesired - avgCurrent;
    for (let i = 0; i < N; i++) {
      yPos[i] += shift;
    }

    // Backward pass: ensure minimum gap is strictly preserved upwards
    for (let i = N - 2; i >= 0; i--) {
      const maxAllowedY =
        yPos[i + 1] - sizeOf(colNodes[i]).height - UNIFORM_GAP;
      if (yPos[i] > maxAllowedY) {
        yPos[i] = maxAllowedY;
      }
    }

    colNodes.forEach((n, idx) => {
      n.position.y = yPos[idx];
      delete n._desiredY;
    });
  });

  return { nodes, edges };
};

// --- Icons ---
const ListIcon = ({ className = 'w-5 h-5', color = '#43B02A' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);
const MessageCircleIcon = ({ className = 'w-5 h-5', color = '#43B02A' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);
const CheckSquareIcon = ({ className = 'w-5 h-5', color = '#43B02A' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);
const ClipboardIcon = ({ className = 'w-5 h-5', color = '#0D9488' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 12h6" />
    <path d="M9 16h6" />
  </svg>
);

const CalendarIcon = ({ className = 'w-5 h-5', color = '#43B02A' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2.5" ry="2.5" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <circle cx="8" cy="14" r="0.75" fill={color} />
    <circle cx="12" cy="14" r="0.75" fill={color} />
    <circle cx="16" cy="14" r="0.75" fill={color} />
    <circle cx="8" cy="17.5" r="0.75" fill={color} />
    <circle cx="12" cy="17.5" r="0.75" fill={color} />
    <circle cx="16" cy="17.5" r="0.75" fill={color} />
  </svg>
);

// ─── Flow Builder Theme & Design System Tokens ─────────────────
// eslint-disable-next-line react-refresh/only-export-components
export const FLOW_THEME = {
  // Palette & Brand Colors
  nodeBgColor: '#FFFFFF',
  nodeBgClass:
    'bg-white border-y border-r border-[#64748B] shadow-[0_1px_2px_rgba(15,23,42,0.12),0_4px_14px_rgba(15,23,42,0.1)]',
  edgeColor: '#1E293B',
  edgeWidth: 2,
  canvasBg: '#FAFBFF',
  optionsBgClass: 'bg-[#F8FAFC]',
  optionCardClass: 'bg-white border border-[#E2E8F0]',
  iconSquareBgClass: 'bg-[#E8F5E6]',
  badgeBgClass: 'bg-[#F4F6F8] border border-[#E6E8EC]',
  badgeTextClass: 'text-[#1E2340]',
  dividerBgClass: 'bg-[#E6E8EC]',

  // Typography Tokens
  titleFont: 'text-[14px] font-medium text-[#1E2340]',
  triggerTitleFont: 'text-[18px] font-semibold text-[#1E2340]',
  questionFont: 'text-[15px] font-medium text-[#3D4458]',
  badgeFont: 'text-[12px] font-medium',
  optionTextFont: 'text-xs font-medium text-[#1E2340]',

  // Dimensions & Radius Tokens
  nodeWidthClass: 'w-[344px]',
  nodeRadiusClass: 'rounded-[16px]',
  iconSquareClass: 'w-[38px] h-[38px] min-w-[38px] min-h-[38px] rounded-[8px]',
  badgeRadiusClass: 'rounded-[6px]',
  optionsContainerRadius: 'rounded-b-[16px]',
};

// --- Custom Node Implementation ---
const handleVisibilityClass = (isConnecting, isLinked) => {
  if (isConnecting)
    return '!w-3 !h-3 !rounded-full !border-2 !border-white opacity-100 shadow-sm';
  if (isLinked) {
    return '!w-[3px] !h-3.5 !min-w-0 !min-h-0 !rounded-[1px] !border-0 opacity-100';
  }
  return '!w-3 !h-3 !rounded-full !border-2 !border-white opacity-100 shadow-sm';
};

const HANDLE_CLASS = '!bg-[#334155] z-20';

function nodeAccentFromData(data) {
  if (!data || data.isStart) return '#43B02A';
  const step = data.fullStep;
  if (isGoogleSheetStep(step)) return '#2563EB';
  if (step?.is_quotation_step) return '#9333EA';
  if (step?.is_datetime_step) return '#D97706';
  if (step?.is_confirmation_step) return '#059669';
  if (data.hasOptions) return '#7C3AED';
  if (step?.is_open_ended) return '#0891B2';
  return '#2563EB';
}

// Trigger node — solid green card (#43B02A) matching Figma Dev Mode specs
const StartNode = ({ data, isConnectable }) => {
  const { inProgress: isConnecting } = useConnection();

  return (
    <div
      className={`group relative flex items-center gap-3 ${FLOW_THEME.nodeBgClass} border-l-4 border-l-[#43B02A] ${FLOW_THEME.nodeRadiusClass} ${FLOW_THEME.nodeWidthClass} p-4 transition-all cursor-pointer`}
      style={
        data.selected
          ? {
              borderTopColor: '#43B02A',
              borderRightColor: '#43B02A',
              borderBottomColor: '#43B02A',
            }
          : undefined
      }
    >
      <div
        className={`${FLOW_THEME.iconSquareClass} ${FLOW_THEME.iconSquareBgClass} flex items-center justify-center shrink-0 shadow-sm p-1.5`}
      >
        <img
          src={triggerIcon}
          alt="Trigger"
          className="w-full h-full object-contain"
        />
      </div>

      <div
        className={`w-[1.5px] h-[44px] ${FLOW_THEME.dividerBgClass} shrink-0`}
      />

      <div className="flex-1 flex flex-col justify-center min-h-[44px] min-w-0 pr-1 text-left py-0.5">
        <span className={`${FLOW_THEME.titleFont} leading-tight mb-0.5`}>
          Trigger
        </span>
        <span
          className={`${FLOW_THEME.triggerTitleFont} leading-normal truncate pb-0.5`}
          title={data.label}
        >
          {data.label || 'Trigger'}
        </span>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        id="main_out"
        isConnectable={isConnectable}
        className={`${HANDLE_CLASS} ${handleVisibilityClass(isConnecting, data.hasOutgoing)}`}
        style={{ top: '50%', right: 0 }}
      />
    </div>
  );
};

// Step nodes — solid green compact card (#43B02A) matching Figma Dev Mode specs
const StepNode = ({ id, data, isConnectable }) => {
  const { inProgress: isConnecting } = useConnection();
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeRef = useRef(null);
  const bodyRef = useRef(null);
  const [mainOutTop, setMainOutTop] = useState(null);

  useLayoutEffect(() => {
    if (data.hasOptions) return;
    const nodeEl = nodeRef.current;
    if (!nodeEl) return;
    const nodeRect = nodeEl.getBoundingClientRect();
    const top = nodeRect.height / 2;
    setMainOutTop((prev) =>
      prev != null && Math.abs(prev - top) < 0.5 ? prev : top,
    );
  }, [
    data.hasOptions,
    data.label,
    data.fullStep?.question_text,
    data.fullStep?.tags,
    data.fullStep?.is_quotation_step,
    data.fullStep?.quotation_title,
    data.fullStep?.is_google_sheet_step,
    data.fullStep?.google_sheet_lookup_column,
    data.fullStep?.is_windex_step,
    data.branchRoutes?.length,
  ]);

  useLayoutEffect(() => {
    if (mainOutTop == null || !id) return;
    updateNodeInternals(id);
  }, [id, mainOutTop, updateNodeInternals]);

  const stepTitle = data.stepType || 'Step';
  const isQuotation = !!data.fullStep?.is_quotation_step;
  const isSheet = isGoogleSheetStep(data.fullStep);
  const rawQuestion = data.fullStep?.question_text || data.label || '';
  const cleanQuestion = stripMarkdown(rawQuestion);
  const questionText =
    cleanQuestion.startsWith('Step ') || cleanQuestion === 'New Question'
      ? data.fullStep?.is_datetime_step
        ? 'please book you demo'
        : cleanQuestion
      : cleanQuestion;
  const stepOrder = data.fullStep?.order ?? 1;
  const quotationSubtitle = isQuotation
    ? data.fullStep?.quotation_title || 'No catalog selected'
    : isSheet
      ? data.fullStep?.google_sheet_lookup_column
        ? `Match ${data.fullStep.google_sheet_lookup_column}`
        : 'No column selected'
      : null;
  const nodeBgClass = FLOW_THEME.nodeBgClass;
  const badgeTextClass = FLOW_THEME.badgeTextClass;
  const accentColor = nodeAccentFromData(data);
  const accentClass = isSheet
    ? 'border-l-[#2563EB]'
    : isQuotation
      ? 'border-l-[#9333EA]'
      : data.fullStep?.is_datetime_step
        ? 'border-l-[#D97706]'
        : data.fullStep?.is_confirmation_step
          ? 'border-l-[#059669]'
          : data.hasOptions
            ? 'border-l-[#7C3AED]'
            : data.fullStep?.is_open_ended
              ? 'border-l-[#0891B2]'
              : 'border-l-[#2563EB]';
  const iconTileClass = isSheet
    ? 'bg-[#EFF6FF] [&_svg]:!stroke-[#2563EB]'
    : isQuotation
      ? 'bg-[#FAF5FF] [&_svg]:!stroke-[#9333EA]'
      : data.fullStep?.is_datetime_step
        ? 'bg-[#FFFBEB] [&_svg]:!stroke-[#D97706]'
        : data.fullStep?.is_confirmation_step
          ? 'bg-[#ECFDF5] [&_svg]:!stroke-[#059669]'
          : data.hasOptions
            ? 'bg-[#F5F3FF] [&_svg]:!stroke-[#7C3AED]'
            : data.fullStep?.is_open_ended
              ? 'bg-[#ECFEFF] [&_svg]:!stroke-[#0891B2]'
              : 'bg-[#EFF6FF] [&_svg]:!stroke-[#2563EB]';

  return (
    <div
      ref={nodeRef}
      className={`group relative flex flex-col ${nodeBgClass} border-l-4 ${accentClass} ${FLOW_THEME.nodeRadiusClass} ${FLOW_THEME.nodeWidthClass} transition-all cursor-pointer`}
      style={
        data.selected
          ? {
              borderTopColor: accentColor,
              borderRightColor: accentColor,
              borderBottomColor: accentColor,
            }
          : undefined
      }
    >
      <Handle
        type="target"
        position={Position.Left}
        id="main_in"
        isConnectable={isConnectable}
        className={`${HANDLE_CLASS} ${handleVisibilityClass(isConnecting, false)}`}
        style={{ top: data.hasOptions ? '34px' : '50%', left: 0 }}
      />

      <div className="flex items-center gap-3 p-4 select-none">
        <div
          className={`${FLOW_THEME.iconSquareClass} ${iconTileClass} flex items-center justify-center shrink-0 p-1.5`}
        >
          {data.icon}
        </div>

        <div
          className={`w-[1.5px] h-[44px] ${FLOW_THEME.dividerBgClass} shrink-0`}
        />

        <div className="flex-1 flex flex-col justify-center min-h-[44px] min-w-0 pr-1 text-left py-0.5">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className={`${FLOW_THEME.titleFont} leading-tight truncate`}>
              {stepTitle}
            </span>
            <div
              className={`${FLOW_THEME.badgeBgClass} ${badgeTextClass} ${FLOW_THEME.badgeFont} ${FLOW_THEME.badgeRadiusClass} px-2.5 py-0.5 shadow-sm whitespace-nowrap shrink-0`}
            >
              Order : {stepOrder}
            </div>
          </div>
          <div className={`${FLOW_THEME.questionFont} min-w-0`}>
            <span
              className="block overflow-hidden"
              style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                lineHeight: '20px',
                maxHeight: '40px',
                overflow: 'hidden',
              }}
              title={questionText}
            >
              {questionText}
            </span>
          </div>
          {quotationSubtitle && (
            <span
              className="text-[11px] text-[#5C6578] leading-tight truncate"
              title={quotationSubtitle}
            >
              {quotationSubtitle}
            </span>
          )}
          <CanvasTagChips
            tags={data.fullStep?.tags}
            variant="onLight"
            className="mt-1"
          />
        </div>
      </div>

      {data.hasOptions && (
        <div
          ref={bodyRef}
          className={`mx-1.5 mb-1.5 p-2 ${FLOW_THEME.optionsBgClass} ${FLOW_THEME.optionsContainerRadius} flex flex-col gap-1.5`}
        >
          {data.fullStep?.options &&
            data.fullStep.options.map((opt) => {
              const isLinked = !!opt.next_step_order;
              return (
                <div
                  key={opt.number}
                  className={`relative flex items-center justify-between ${FLOW_THEME.optionCardClass} px-3 py-2 rounded-lg ${FLOW_THEME.optionTextFont} hover:bg-[#F8FAFC] transition-colors`}
                >
                  <div className="min-w-0 flex-1 pr-2 flex flex-col gap-1">
                    <span className="truncate">
                      {opt.text || `Option ${opt.number}`}
                    </span>
                    <CanvasTagChips tags={opt.tags} variant="onLight" />
                    {opt.stage?.name && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-text-secondary">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{
                            backgroundColor: opt.stage.color || '#94A3B8',
                          }}
                        />
                        <span className="truncate">{opt.stage.name}</span>
                      </span>
                    )}
                  </div>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`opt_${opt.number}`}
                    isConnectable={isConnectable}
                    className={`${HANDLE_CLASS} ${handleVisibilityClass(isConnecting, isLinked)}`}
                    style={{ top: '50%', right: '-7px' }}
                  />
                </div>
              );
            })}
        </div>
      )}

      {data.branchRoutes?.length > 0 && (
        <div
          className={`mx-1.5 mb-1.5 p-2 ${FLOW_THEME.optionsBgClass} ${FLOW_THEME.optionsContainerRadius} flex flex-col gap-1.5`}
        >
          {data.branchRoutes.map((route) => (
            <div
              key={route.id}
              className={`relative flex items-center justify-between ${FLOW_THEME.optionCardClass} px-3 py-2 rounded-lg ${FLOW_THEME.optionTextFont}`}
            >
              <span className="truncate">{route.label}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={route.id}
                isConnectable={isConnectable}
                className={`${HANDLE_CLASS} ${handleVisibilityClass(isConnecting, route.linked)}`}
                style={{ top: '50%', right: '-7px' }}
              />
            </div>
          ))}
        </div>
      )}

      {!data.hasOptions && !data.branchRoutes?.length && (
        <Handle
          type="source"
          position={Position.Right}
          id="main_out"
          isConnectable={isConnectable}
          className={`${HANDLE_CLASS} ${handleVisibilityClass(isConnecting, data.hasOutgoing)}`}
          style={{ top: '50%', right: 0 }}
        />
      )}
    </div>
  );
};

const CustomNode = ({ id, data, isConnectable }) =>
  data.isStart ? (
    <StartNode data={data} isConnectable={isConnectable} />
  ) : (
    <StepNode id={id} data={data} isConnectable={isConnectable} />
  );

function markEdgeHandles(
  source,
  sourceHandle,
  target,
  targetHandle,
  on,
  color,
) {
  [
    [source, sourceHandle],
    [target, targetHandle],
  ].forEach(([nodeId, handleId]) => {
    if (!nodeId || !handleId) return;
    const el = document.querySelector(
      `.react-flow__node[data-id="${nodeId}"] .react-flow__handle[data-handleid="${handleId}"]`,
    );
    if (!el) return;
    el.style.outline = on ? `2px solid ${color}` : '';
    el.style.outlineOffset = on ? '1px' : '';
  });
}

const OptionEdge = ({
  id,
  source,
  target,
  sourceHandle,
  targetHandle,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  data,
  style,
  markerEnd,
}) => {
  const { getNode, getNodes, getEdges, setCenter, getZoom } = useReactFlow();
  const [hovered, setHovered] = useState(false);
  const dy = targetY - sourceY;
  const absDy = Math.abs(dy);

  const labelText = typeof label === 'string' ? label : '';

  // Starting Space (હેન્ડલ અને બેજ વચ્ચેની જગ્યા): 40px ફિક્સ
  const labelMarginLeft = 40;

  // Find longest option length across all options on this node (or fallback to current label)
  const maxOptionLen =
    data?.maxOptionLength || (labelText ? labelText.length : 10);
  const maxEstBadgeWidth = Math.max(50, maxOptionLen * 7.5 + 28);
  const maxBadgeEndFromSource = labelMarginLeft + maxEstBadgeWidth;

  // After Space (સૌથી લાંબા બેજ પછી લાઇન ટર્ન સુધીની જગ્યા): + 10%
  const extraSpace10Pct = Math.max(10, maxEstBadgeWidth * 0.1);
  const baseSplitFromSource = maxBadgeEndFromSource + extraSpace10Pct;

  const optNum =
    data?.optIndex != null
      ? data.optIndex
      : parseInt((id || '').replace(/.*opt_?(\d+).*/, '$1'), 10) - 1 || 0;
  const optIndex = Math.max(0, optNum);
  const totalOpts = data?.totalOptions || optIndex + 1;

  // Click on badge to smoothly redirect/pan/focus viewport to connected target node
  const handleBadgeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!target) return;
    const targetNode = typeof getNode === 'function' ? getNode(target) : null;
    if (targetNode && typeof setCenter === 'function') {
      const nodeW = targetNode.measured?.width || targetNode.width || 344;
      const nodeH = targetNode.measured?.height || targetNode.height || 150;
      const targetCenterX = (targetNode.position?.x ?? 0) + nodeW / 2;
      const targetCenterY = (targetNode.position?.y ?? 0) + nodeH / 2;
      const currentZoom = typeof getZoom === 'function' ? getZoom() : 0.8;

      setCenter(targetCenterX, targetCenterY, {
        zoom: Math.min(Math.max(currentZoom, 0.75), 1.0),
        duration: 450,
      });

      window.dispatchEvent(
        new CustomEvent('flow:select-node', { detail: { nodeId: target } }),
      );
    }
  };

  const r = 16;
  const allNodes = typeof getNodes === 'function' ? getNodes() : [];
  const allEdges = typeof getEdges === 'function' ? getEdges() : [];
  const nodeMap = new Map(allNodes.map((n) => [n.id, n]));

  const sourceNode =
    nodeMap.get(source) ||
    (typeof getNode === 'function' ? getNode(source) : null);
  const targetNode =
    nodeMap.get(target) ||
    (typeof getNode === 'function' ? getNode(target) : null);
  const sourceXPos = sourceNode?.position?.x ?? sourceX;
  const targetXPos = targetNode?.position?.x ?? targetX;

  // An edge is only backward if target is positioned behind (to the left) or in the same column as source
  const isBackward = targetXPos <= sourceXPos + 40 || targetX <= sourceX + 20;

  const nodeSelected = (node) => !!node?.data?.selected;
  const linked = nodeSelected(sourceNode) || nodeSelected(targetNode);
  const accent = nodeAccentFromData(
    (nodeSelected(sourceNode) ? sourceNode : targetNode)?.data,
  );
  const FORWARD_COLOR = linked ? accent : '#1E293B';
  const RETURN_COLOR = linked ? accent : '#1E293B';
  const edgeOpacity = 1;
  const edgeWidth = 2;

  if (!isBackward) {
    const isForwardEdge = (edge) => {
      const from = nodeMap.get(edge.source);
      const to = nodeMap.get(edge.target);
      return (to?.position?.x ?? 0) > (from?.position?.x ?? 0) + 40;
    };
    const fromHere = allEdges.filter(
      (edge) => edge.source === source && isForwardEdge(edge),
    );
    const STUB = 12;
    const PILL_GAP = 16;
    const LANE = 22;
    const PILL_H = 22;
    const pillWidthFor = (text) =>
      Math.min(240, Math.max(44, String(text || '').length * 7.2 + 28));
    const ownPill = pillWidthFor(labelText);
    const widestPill = fromHere.reduce(
      (max, edge) =>
        Math.max(
          max,
          pillWidthFor(typeof edge.label === 'string' ? edge.label : ''),
        ),
      ownPill,
    );
    const ordered = [...fromHere].sort(
      (a, b) =>
        (a.data?.optIndex ?? 0) - (b.data?.optIndex ?? 0) ||
        String(a.id).localeCompare(String(b.id)),
    );
    const lane = Math.max(
      0,
      ordered.findIndex((edge) => edge.id === id),
    );
    const laneCount = Math.max(1, ordered.length);
    const pillLeft = sourceX + STUB;
    const afterWidest = pillLeft + widestPill + PILL_GAP;
    const gapEnd = Math.max(afterWidest, targetX - 28);
    const room = Math.max(0, gapEnd - afterWidest);
    const pitch = laneCount <= 1 ? 0 : Math.min(LANE, room / (laneCount - 1));
    const laneFromRight = laneCount - 1 - lane;
    const turnX = afterWidest + laneFromRight * pitch;

    let edgePath = '';
    const span = Math.max(0, targetX - turnX);
    if (absDy < 4 || span < 8) {
      edgePath = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
    } else {
      const dir = sourceY < targetY ? 1 : -1;
      const cr = Math.min(10, absDy / 2, span / 2);
      edgePath = `M ${sourceX} ${sourceY} L ${turnX - cr} ${sourceY} Q ${turnX} ${sourceY} ${turnX} ${sourceY + dir * cr} L ${turnX} ${targetY - dir * cr} Q ${turnX} ${targetY} ${Math.min(turnX + cr, targetX)} ${targetY} L ${targetX} ${targetY}`;
    }
    const traceColor = hovered && !linked ? '#2563EB' : FORWARD_COLOR;
    const liveMarker =
      markerEnd && typeof markerEnd === 'object'
        ? { ...markerEnd, color: traceColor }
        : markerEnd;

    return (
      <>
        <path
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={18}
          className="react-flow__edge-interaction"
          onMouseEnter={() => {
            setHovered(true);
            markEdgeHandles(
              source,
              sourceHandle,
              target,
              targetHandle,
              true,
              linked ? accent : '#2563EB',
            );
          }}
          onMouseLeave={() => {
            setHovered(false);
            markEdgeHandles(source, sourceHandle, target, targetHandle, false);
          }}
        />
        <path
          id={id}
          style={{
            ...style,
            stroke: traceColor,
            strokeWidth: edgeWidth,
            opacity: edgeOpacity,
            pointerEvents: 'none',
          }}
          className="react-flow__edge-path"
          d={edgePath}
          markerEnd={liveMarker}
        />
        {label && (
          <EdgeLabelRenderer>
            <div
              style={{
                position: 'absolute',
                transform: `translate(${pillLeft}px, ${sourceY - PILL_H / 2}px)`,
                borderColor: linked ? accent : undefined,
                pointerEvents: 'all',
                maxWidth: labelText.length > 36 ? 240 : undefined,
              }}
              onClick={handleBadgeClick}
              className="nodrag nopan box-border bg-white text-[#0F172A] text-[11px] font-medium leading-none px-2 h-[22px] rounded-md border border-[#CBD5E1] shadow-sm z-10 inline-flex items-center justify-center cursor-pointer hover:border-[#43B02A] transition-colors select-none whitespace-nowrap"
              title={labelText || 'Click to jump to connected step'}
            >
              <span
                className={labelText.length > 36 ? 'truncate' : undefined}
                title={labelText}
              >
                {label}
              </span>
            </div>
          </EdgeLabelRenderer>
        )}
      </>
    );
  }

  // --- Return / Backward / Loop edge: Route cleanly AROUND the outside with staggered multi-tracks and indigo color ---
  const sourceTop = sourceNode?.position?.y ?? sourceY - 100;
  const sourceHeight =
    sourceNode?.measured?.height ||
    sourceNode?.height ||
    (sourceNode?.data?.hasOptions
      ? 80 + (sourceNode.data.fullStep?.options?.length || 2) * 44
      : 110);
  const sourceBottom = sourceTop + sourceHeight;

  const targetTop = targetNode?.position?.y ?? targetY - 50;
  const targetHeight =
    targetNode?.measured?.height ||
    targetNode?.height ||
    (targetNode?.data?.hasOptions
      ? 80 + (targetNode.data.fullStep?.options?.length || 2) * 44
      : 110);
  const targetBottom = targetTop + targetHeight;

  // Find all nodes in the horizontal span between source and target to avoid intersecting ANY node
  const minSpanX = Math.min(sourceX, targetX) - 100;
  const maxSpanX = Math.max(sourceX, targetX) + baseSplitFromSource + 150;
  const spanNodes = allNodes.filter((n) => {
    const nx = n.position?.x ?? 0;
    const nw = n.measured?.width || 344;
    return nx + nw >= minSpanX && nx <= maxSpanX;
  });

  const spanBottom = spanNodes.reduce(
    (max, n) =>
      Math.max(
        max,
        (n.position?.y ?? max) + (n.measured?.height || n.height || 150),
      ),
    Math.max(sourceBottom, targetBottom),
  );

  // Deterministically sort all return edges in graph so EVERY return edge has a unique, non-overlapping channel track
  const returnEdges = allEdges.filter((e) => {
    const sn = nodeMap.get(e.source);
    const tn = nodeMap.get(e.target);
    const sx = sn?.position?.x ?? 0;
    const tx = tn?.position?.x ?? 0;
    return tx <= sx + 40;
  });

  returnEdges.sort((a, b) => {
    const aSn = nodeMap.get(a.source);
    const bSn = nodeMap.get(b.source);
    const aY = aSn?.position?.y ?? 0;
    const bY = bSn?.position?.y ?? 0;
    if (Math.abs(aY - bY) > 5) return aY - bY;
    const aOpt =
      a.data?.optIndex != null
        ? a.data.optIndex
        : parseInt((a.sourceHandle || '').replace(/\D/g, ''), 10) || 0;
    const bOpt =
      b.data?.optIndex != null
        ? b.data.optIndex
        : parseInt((b.sourceHandle || '').replace(/\D/g, ''), 10) || 0;
    return aOpt - bOpt;
  });

  const foundGlobalIdx = returnEdges.findIndex((e) => e.id === id);
  const returnGlobalIdx =
    foundGlobalIdx >= 0
      ? foundGlobalIdx
      : optIndex + (parseInt((source || '').replace(/\D/g, ''), 10) || 0);

  const RETURN_TRACK_GAP = 16;
  const loopY = spanBottom + 45 + returnGlobalIdx * RETURN_TRACK_GAP;

  // Stagger vertical drop X coordinate so upper nodes in the same column drop OUTSIDE lower nodes' badges
  const sourceColNodes = allNodes.filter(
    (n) => Math.abs((n.position?.x ?? 0) - sourceXPos) < 100,
  );
  sourceColNodes.sort((a, b) => (a.position?.y ?? 0) - (b.position?.y ?? 0));
  const colIndex = sourceColNodes.findIndex((n) => n.id === source);
  const totalNodesInCol = Math.max(1, sourceColNodes.length);

  const nodeRankFromBottom = Math.max(0, totalNodesInCol - 1 - colIndex);
  const colNodeStaggerX =
    nodeRankFromBottom * (Math.max(totalOpts, 3) * 14 + 18);
  const optStaggerX = optIndex * 14;

  const splitX =
    sourceX +
    (labelText ? baseSplitFromSource : 32) +
    colNodeStaggerX +
    optStaggerX;

  const targetColNodes = allNodes.filter(
    (n) =>
      Math.abs((n.position?.x ?? 0) - (targetNode?.position?.x ?? targetX)) <
      100,
  );
  const targetColLeft =
    targetColNodes.length > 0
      ? Math.min(...targetColNodes.map((n) => n.position?.x ?? targetX))
      : (targetNode?.position?.x ?? targetX);
  const leftX = targetColLeft - 32 - returnGlobalIdx * 14;

  const loopPath =
    `M ${sourceX} ${sourceY} ` +
    `L ${splitX - r} ${sourceY} Q ${splitX} ${sourceY} ${splitX} ${sourceY + r} ` +
    `L ${splitX} ${loopY - r} Q ${splitX} ${loopY} ${splitX - r} ${loopY} ` +
    `L ${leftX + r} ${loopY} Q ${leftX} ${loopY} ${leftX} ${loopY - r} ` +
    `L ${leftX} ${targetY + r} Q ${leftX} ${targetY} ${leftX + r} ${targetY} ` +
    `L ${targetX} ${targetY}`;

  const returnMarkerId = `return-arrow-${id.replace(/[^a-zA-Z0-9-_]/g, '_')}`;

  return (
    <>
      <svg style={{ height: 0, width: 0, position: 'absolute' }}>
        <defs>
          <marker
            id={returnMarkerId}
            viewBox="0 0 10 10"
            refX="6"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill={RETURN_COLOR} />
          </marker>
        </defs>
      </svg>
      <path
        id={id}
        style={{
          ...style,
          stroke: RETURN_COLOR,
          strokeWidth: edgeWidth,
          strokeDasharray: '6 4',
          opacity: edgeOpacity,
        }}
        className="react-flow__edge-path"
        d={loopPath}
        markerEnd={`url(#${returnMarkerId})`}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(0, -50%) translate(${sourceX + labelMarginLeft}px, ${sourceY}px)`,
              pointerEvents: 'all',
            }}
            onClick={handleBadgeClick}
            className="nodrag nopan bg-white text-[#1E2340] text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-[#E2E8F0] shadow-sm max-w-[160px] z-10 flex items-center gap-1.5 cursor-pointer hover:border-[#43B02A] transition-colors select-none"
            title={`Click to jump back to connected step`}
          >
            <svg
              className="w-3.5 h-3.5 text-[#1E2340] shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 14 4 9 9 4" />
              <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
            </svg>
            <span className="truncate" title={label}>
              {label}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const nodeTypes = {
  custom: CustomNode,
};
const edgeTypes = {
  optionEdge: OptionEdge,
};
export default function FlowBuilder({
  onSave,
  onCancel,
  initialFlow,
  isSaving = false,
  companyId,
  canEdit = false,
  canSave = false,
  isExporting = false,
}) {
  const [flow, setFlow] = useState(
    () =>
      hydrateAutomationTags(initialFlow) || {
        name: 'New Automation',
        keywords: '',
        welcome_message: '',
        completion_message: '',
        is_default: false,
        is_draft: true,
        steps: [],
      },
  );

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showSimulator, setShowSimulator] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isViewReady, setIsViewReady] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [importError, setImportError] = useState('');
  const [highlightQuotationErrors, setHighlightQuotationErrors] =
    useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [optionLeadKey, setOptionLeadKey] = useState(null);
  const [setupKind, setSetupKind] = useState(null);
  const { hasFeature } = useFeatures();
  const canUseQuotations = hasFeature('quotations');
  const { data: pipelineStagesData } = useQuery({
    queryKey: ['crm-pipeline-stages', companyId],
    queryFn: () => getCRMPipelineStages(companyId),
    enabled: !!companyId,
    staleTime: 60_000,
  });
  const pipelineStages = Array.isArray(pipelineStagesData)
    ? pipelineStagesData
    : pipelineStagesData?.results || pipelineStagesData?.stages || [];
  const { data: windexStatus } = useQuery({
    queryKey: ['windex-status', companyId],
    queryFn: () => getWindexStatus(companyId),
    enabled: !!companyId,
    staleTime: 30_000,
  });
  const windexConnected = !!windexStatus?.is_connected;
  const pendingImportLayoutRef = useRef(false);
  const pendingImportNodeIdsRef = useRef(null);
  const pendingImportMeasuredRef = useRef(new Set());
  const autoLayoutRef = useRef(() => {});
  const editStepScrollRef = useRef(null);

  const handleSelectStepFromSearch = useCallback((stepOrder) => {
    const nodeId = `step_${stepOrder}`;
    setSelectedNodeId(nodeId);

    const instance = reactFlowInstanceRef.current;
    if (instance) {
      const targetNode = instance.getNode(nodeId);
      if (targetNode) {
        const nodeW = targetNode.measured?.width || targetNode.width || 344;
        const nodeH = targetNode.measured?.height || targetNode.height || 150;
        const targetCenterX = (targetNode.position?.x ?? 0) + nodeW / 2;
        const targetCenterY = (targetNode.position?.y ?? 0) + nodeH / 2;
        const currentZoom =
          typeof instance.getZoom === 'function' ? instance.getZoom() : 0.8;

        instance.setCenter(targetCenterX, targetCenterY, {
          zoom: Math.min(Math.max(currentZoom, 0.75), 1.0),
          duration: 450,
        });

        window.dispatchEvent(
          new CustomEvent('flow:select-node', { detail: { nodeId } }),
        );
      }
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Toggle search modal with Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowSearchModal((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Baseline to detect unsaved changes against — captured once, on mount.
  const initialFlowRef = useRef(flow);
  const isDirty =
    JSON.stringify(flow) !== JSON.stringify(initialFlowRef.current);

  useEffect(() => {
    if (!initialFlow) return;
    const incoming = hydrateAutomationTags(initialFlow);
    setFlow((prev) => {
      const sameSession = !prev.id || prev.id === incoming.id;
      if (sameSession) {
        const merged = {
          ...prev,
          id: incoming.id ?? prev.id,
          keywords:
            typeof incoming.keywords === 'string'
              ? incoming.keywords
              : (incoming.keywords ?? incoming.keyword ?? prev.keywords),
          is_draft: incoming.is_draft ?? prev.is_draft,
          is_active: incoming.is_active ?? prev.is_active,
          updated_at: incoming.updated_at ?? prev.updated_at,
          steps: Array.isArray(incoming.steps)
            ? applyRemoteStepTags(prev.steps, incoming.steps)
            : prev.steps,
        };
        initialFlowRef.current = merged;
        return merged;
      }
      initialFlowRef.current = incoming;
      return incoming;
    });
  }, [initialFlow]);

  useEffect(() => {
    const handleSelectNodeEvent = (e) => {
      if (e.detail?.nodeId) {
        setSelectedNodeId(e.detail.nodeId);
      }
    };
    window.addEventListener('flow:select-node', handleSelectNodeEvent);
    return () => {
      window.removeEventListener('flow:select-node', handleSelectNodeEvent);
    };
  }, []);

  const openUnsavedConfirm = useCallback(() => {
    setShowUnsavedConfirm(true);
  }, []);

  const { requestLeave, confirmLeave, cancelLeave } = useUnsavedChangesGuard({
    isDirty,
    onBlock: openUnsavedConfirm,
  });

  const handleBackClick = () => {
    if (isDirty) {
      requestLeave(() => onCancel());
    } else {
      onCancel();
    }
  };

  const persistFlow = (asDraft) => {
    const steps = flow.steps || [];

    if (!asDraft) {
      if (!(flow.name || '').trim()) {
        toast.error('Automation name is required to publish.');
        return false;
      }
    }

    for (const step of steps) {
      if (
        !step.is_datetime_step &&
        !step.is_quotation_step &&
        !isGoogleSheetStep(step) &&
        !isWindexStep(step) &&
        (step.options || []).length > 10
      ) {
        toast.error(`Step ${step.order}: Maximum 10 options allowed.`);
        setSelectedNodeId(`step_${step.order}`);
        return false;
      }
      if (step.is_quotation_step) {
        const result = validateQuotationStep(step);
        if (!result.ok) {
          toast.error(result.error);
          setHighlightQuotationErrors(true);
          setSelectedNodeId(`step_${step.order}`);
          return false;
        }
      }
      if (step.is_datetime_step) {
        const result = validateDateTimeStep(step);
        if (!result.ok) {
          toast.error(result.error);
          setSelectedNodeId(`step_${step.order}`);
          return false;
        }
      }
      if (isWindexStep(step)) {
        const result = validateWindexStep(step);
        if (!result.ok) {
          toast.error(result.error);
          setHighlightQuotationErrors(true);
          setSelectedNodeId(`step_${step.order}`);
          return false;
        }
      }
      if (isGoogleSheetStep(step)) {
        const result = validateGoogleSheetStep(step);
        if (!result.ok) {
          toast.error(result.error);
          setHighlightQuotationErrors(true);
          setSelectedNodeId(`step_${step.order}`);
          return false;
        }
      }
      if (
        !asDraft &&
        !isGoogleSheetStep(step) &&
        !isWindexStep(step) &&
        !(step.question_text || '').trim()
      ) {
        toast.error(
          `Step ${step.order}: Question text is required to publish.`,
        );
        setSelectedNodeId(`step_${step.order}`);
        return false;
      }
    }

    setHighlightQuotationErrors(false);

    const flowWithoutReminder = { ...flow };
    delete flowWithoutReminder.reminder_enabled;
    delete flowWithoutReminder.reminder_delay_minutes;
    delete flowWithoutReminder.reminder_message;
    delete flowWithoutReminder.reminder_stage_id;
    const cleanedSteps = cleanFlowSteps(steps);
    const keywords =
      typeof flow.keywords === 'string' ? flow.keywords : flow.keywords || '';
    const payload = asDraft
      ? {
          ...flowWithoutReminder,
          keywords,
          keyword: keywords,
          steps: cleanedSteps,
          is_draft: true,
          is_active: false,
        }
      : {
          ...flowWithoutReminder,
          keywords,
          keyword: keywords,
          steps: cleanedSteps,
          is_draft: false,
          is_active: true,
        };
    onSave(payload, { asDraft });
    return true;
  };

  const exportCanvasAsJson = () => {
    const instance = reactFlowInstanceRef.current;
    const currentNodes = instance ? instance.getNodes() : nodes;
    try {
      const exportedData = serializeCanvasFlow(flow, currentNodes);
      downloadJsonFile(exportedData, flow.name || 'flow');
      toast.success('Flow exported');
    } catch (err) {
      toast.error(err.message || 'Failed to export flow.');
    }
  };

  const handleExportClick = () => {
    exportCanvasAsJson();
  };

  const applyParsedFlowToCanvas = useCallback((parsed) => {
    explicitNodePositionsRef.current = new Map();
    const ids = new Set(['start']);
    (parsed.flow.steps || []).forEach((step) => {
      if (step.order == null) return;
      ids.add(step.node_id || `step_${step.order}`);
    });
    pendingImportNodeIdsRef.current = ids;
    pendingImportMeasuredRef.current = new Set();
    initialized.current = true;
    pendingImportLayoutRef.current = true;
    hasFitViewRef.current = false;

    setIsViewReady(false);
    setSelectedNodeId(null);
    setFlow((prev) => ({
      ...parsed.flow,
      ...(prev?.id != null ? { id: prev.id } : {}),
      is_draft: prev?.is_draft ?? true,
      is_active: prev?.is_active ?? false,
      is_default: prev?.is_default ?? false,
    }));
  }, []);

  const importJsonToCanvas = useCallback(
    async ({ file, data } = {}) => {
      if (!file && data == null) return;
      setImportError('');
      try {
        const parsed = parseFlowDocument(
          data != null ? data : await file.text(),
        );
        applyParsedFlowToCanvas(parsed);
        toast.success('Flow loaded onto canvas');
        setShowImportModal(false);
      } catch (err) {
        setImportError(err.message || 'Failed to parse JSON.');
      }
    },
    [applyParsedFlowToCanvas],
  );

  const canvasHasNodesToReplace = (flow.steps || []).length > 0;

  const handleImportClick = () => {
    if (!canEdit) return;
    setImportError('');
    setShowImportModal(true);
  };

  const handleImportModalClose = () => {
    setShowImportModal(false);
    setImportError('');
  };

  const handleMediaUpload = async (e) => {
    if (!canEdit) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingFile(true);
    const uploadToastId = toast.loading('Uploading file...');
    try {
      const response = await uploadWhatsAppFlowMedia({
        companyId,
        file,
      });
      if (response && response.url) {
        updateSelectedStepField('media_url', response.url);
        toast.success('File uploaded successfully!', { id: uploadToastId });
      } else {
        throw new Error('Invalid upload response');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload file. Please try again.', {
        id: uploadToastId,
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  // Track if we need initial layout
  const initialized = useRef(false);
  const reactFlowInstanceRef = useRef(null);
  const hasFitViewRef = useRef(false);
  const explicitNodePositionsRef = useRef(new Map());

  const tryFitView = useCallback((nodeCount) => {
    if (hasFitViewRef.current) return;
    if (!reactFlowInstanceRef.current) return;
    if (nodeCount === 0) return;

    const MAX_RETRIES = 30; // ~500ms budget at 60fps, safety net only

    const attemptFit = (retriesLeft) => {
      const instance = reactFlowInstanceRef.current;
      if (!instance) return;

      const rfNodes = instance.getNodes();
      const allMeasured = rfNodes.every(
        (n) => n.measured?.width && n.measured?.height,
      );

      if (!allMeasured && retriesLeft > 0) {
        requestAnimationFrame(() => attemptFit(retriesLeft - 1));
        return;
      }

      hasFitViewRef.current = true;
      // Import schedules one layout after nodes are measured.
      if (
        explicitNodePositionsRef.current.size === 0 &&
        !pendingImportLayoutRef.current
      ) {
        const laidOut = DagreLayout(rfNodes, instance.getEdges(), 'LR').nodes;
        setNodes(laidOut);
      }
      requestAnimationFrame(() => {
        reactFlowInstanceRef.current?.fitView({
          padding: 0.16,
          maxZoom: 0.85,
          duration: 0,
        });
        setIsViewReady(true);
      });
    };

    requestAnimationFrame(() => attemptFit(MAX_RETRIES));
  }, []);

  const handleDuplicateStep = useCallback(
    (sourceOrder) => {
      if (!canEdit) return;
      const rawOrder =
        typeof sourceOrder === 'string'
          ? sourceOrder.replace('step_', '')
          : sourceOrder;
      const orderNum = parseInt(rawOrder, 10);
      const sourceStep = flow.steps.find(
        (s) => s.order === orderNum || s.order === sourceOrder,
      );
      if (!sourceStep) {
        toast.error('Source step not found.');
        return;
      }

      const newOrder =
        flow.steps.length > 0
          ? Math.max(...flow.steps.map((s) => s.order)) + 1
          : 1;

      // Deep clone options with detached outgoing connections and fresh tag copies
      const clonedOptions = (sourceStep.options || []).map((opt, idx) => ({
        number: opt.number ?? idx + 1,
        text: opt.text || `Option ${idx + 1}`,
        next_step_order: null,
        tags: Array.isArray(opt.tags)
          ? opt.tags.map((t) =>
              typeof t === 'object' && t !== null ? { ...t } : t,
            )
          : [],
        tag_ids: Array.isArray(opt.tag_ids) ? [...opt.tag_ids] : [],
      }));

      // Strip database IDs so the duplicated step is treated as a new entity
      const cleanSource = stripImportedIds(sourceStep);

      // Clone full step configuration with independent tags and no duplicated DB IDs
      const duplicatedStep = {
        ...cleanSource,
        order: newOrder,
        question_text: sourceStep.question_text || '',
        options: clonedOptions,
        tags: Array.isArray(sourceStep.tags)
          ? sourceStep.tags.map((t) =>
              typeof t === 'object' && t !== null ? { ...t } : t,
            )
          : [],
        tag_ids: Array.isArray(sourceStep.tag_ids)
          ? [...sourceStep.tag_ids]
          : [],
        next_step_order: null,
        windex_no_result_next_step_order: null,
        is_open_ended: !!sourceStep.is_open_ended,
        is_confirmation_step: !!sourceStep.is_confirmation_step,
        is_datetime_step: !!sourceStep.is_datetime_step,
        is_quotation_step: !!sourceStep.is_quotation_step,
        is_google_sheet_step: isGoogleSheetStep(sourceStep),
        ...googleSheetFields(sourceStep),
        quotation_id: resolveQuotationId(sourceStep),
        quotation_title: sourceStep.quotation_title || null,
        timezone: sourceStep.timezone || APPOINTMENT_TIMEZONE,
        working_hours_start: sourceStep.working_hours_start || '09:00:00',
        working_hours_end: sourceStep.working_hours_end || '17:00:00',
        break_time_start: sourceStep.break_time_start || null,
        break_time_end: sourceStep.break_time_end || null,
        slot_duration_minutes: sourceStep.slot_duration_minutes || 30,
        available_days_count: sourceStep.available_days_count || 7,
        media_type: sourceStep.media_type || 'text',
        media_url: sourceStep.media_url || '',
      };
      delete duplicatedStep.id;
      delete duplicatedStep.step_id;
      delete duplicatedStep.flow_id;
      delete duplicatedStep.flow;
      delete duplicatedStep.step;
      delete duplicatedStep.created_at;
      delete duplicatedStep.updated_at;
      delete duplicatedStep.node_id;

      // Calculate smart canvas position directly below the source node in the same vertical column
      const instance = reactFlowInstanceRef.current;
      const allRfNodes = instance ? instance.getNodes() : nodes;
      const sourceNode = allRfNodes.find(
        (n) =>
          n.id === `step_${orderNum}` ||
          n.id === `step_${sourceOrder}` ||
          n.id === String(sourceOrder) ||
          n.data?.fullStep?.order === orderNum,
      );

      const getNodeHeight = (n) => {
        if (n?.measured?.height) return n.measured.height;
        if (n?.height) return n.height;
        const optsCount = n?.data?.fullStep?.options?.length || 0;
        if (optsCount > 0) return 76 + optsCount * 46 + 16;
        return 120;
      };

      const getNodeWidth = (n) => {
        return n?.measured?.width || n?.width || 320;
      };

      const sourceX = sourceNode?.position?.x ?? 250;
      const sourceY = sourceNode?.position?.y ?? 100;
      const sourceHeight = sourceNode ? getNodeHeight(sourceNode) : 120;
      const sourceWidth = sourceNode ? getNodeWidth(sourceNode) : 320;

      let targetX = sourceX;
      let targetY = sourceY + sourceHeight + 40;

      // Iteratively check for direct bounding-box collision with existing nodes
      let collision = true;
      let attempts = 0;
      while (collision && attempts < 15) {
        attempts++;
        const overlappingNode = allRfNodes.find((n) => {
          if (
            n.id === sourceNode?.id ||
            n.id === `step_${newOrder}` ||
            n.id === `step_${orderNum}`
          )
            return false;
          const nX = n.position?.x ?? 0;
          const nY = n.position?.y ?? 0;
          const nW = getNodeWidth(n);
          const nH = getNodeHeight(n);

          const horizontalOverlap =
            Math.abs(nX - targetX) < Math.max(nW, sourceWidth) - 40;
          const verticalOverlap =
            nY < targetY + sourceHeight && nY + nH > targetY;

          return horizontalOverlap && verticalOverlap;
        });

        if (overlappingNode) {
          const nY = overlappingNode.position?.y ?? 0;
          const nH = getNodeHeight(overlappingNode);
          targetY = nY + nH + 40;
        } else {
          collision = false;
        }
      }

      explicitNodePositionsRef.current.set(`step_${newOrder}`, {
        x: targetX,
        y: targetY,
      });

      // Smoothly pan & fit canvas to bring the newly duplicated node and source node into full view
      setTimeout(() => {
        if (reactFlowInstanceRef.current) {
          const instance = reactFlowInstanceRef.current;
          if (instance.fitBounds) {
            instance.fitBounds(
              {
                x: targetX - 50,
                y: sourceY - 30,
                width: sourceWidth + 100,
                height: targetY + sourceHeight - sourceY + 60,
              },
              { padding: 0.25, duration: 400 },
            );
          } else if (instance.setCenter) {
            const currentZoom = instance.getZoom?.() || 0.75;
            const midY = (sourceY + targetY + sourceHeight) / 2;
            instance.setCenter(targetX + sourceWidth / 2, midY, {
              zoom: Math.min(Math.max(currentZoom, 0.6), 0.8),
              duration: 350,
            });
          }
        }
      }, 60);

      setFlow((prev) => ({
        ...prev,
        steps: [...prev.steps, duplicatedStep],
      }));

      setSelectedNodeId(`step_${newOrder}`);
      toast.success(`Step ${sourceStep.order} duplicated as Step ${newOrder}`);
    },
    [canEdit, flow, nodes, setFlow, setSelectedNodeId],
  );

  // Derive Nodes & Edges from state without losing user-dragged positions
  const syncGraphWithFlow = useCallback(() => {
    const newNodes = [];
    const newEdges = [];

    // Every step order referenced as a "next step" from somewhere (start,
    // a step's main next-step, or an option) — used to know which nodes'
    // incoming handle already has an edge, for handle visibility.
    const targetedOrders = new Set();
    const firstStep =
      flow.steps.length > 0
        ? flow.steps.find((s) => s.order === 1) || flow.steps[0]
        : null;
    if (firstStep) targetedOrders.add(firstStep.order);
    flow.steps.forEach((step) => {
      if (isGoogleSheetStep(step)) {
        const foundOrder = sheetFoundNextOrder(step);
        const missingOrder = sheetNotFoundNextOrder(step);
        if (foundOrder != null) targetedOrders.add(foundOrder);
        if (missingOrder != null) targetedOrders.add(missingOrder);
      } else if (step.options?.length) {
        step.options.forEach((opt) => {
          if (opt.next_step_order) targetedOrders.add(opt.next_step_order);
        });
      } else if (step.next_step_order) {
        targetedOrders.add(step.next_step_order);
      }
    });

    // 1. Add Start Node
    newNodes.push({
      id: 'start',
      type: 'custom',
      data: {
        isStart: true,
        stepType: 'Trigger',
        label: flow.name,
        icon: (
          <img
            src={triggerIcon}
            alt="Trigger"
            className="w-full h-full object-contain"
          />
        ),
        selected: selectedNodeId === 'start',
        hasOutgoing: !!firstStep,
      },
      position: { x: 0, y: 0 },
    });

    const defaultEdgeStyle = {
      type: 'smoothstep',
      selectable: false,
      animated: false,
      pathOptions: { borderRadius: 16, offset: 0 },
      style: { strokeWidth: 2, stroke: '#1E293B' },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#1E293B',
        width: 18,
        height: 18,
      },
    };

    // Edge for start -> first node
    if (firstStep) {
      const firstTargetId = firstStep.node_id || `step_${firstStep.order}`;
      newEdges.push({
        id: `e_start_${firstTargetId}`,
        source: 'start',
        sourceHandle: 'main_out',
        target: firstTargetId,
        targetHandle: 'main_in',
        ...defaultEdgeStyle,
        style: {
          strokeWidth: 2,
          stroke:
            selectedNodeId === 'start'
              ? '#43B02A'
              : selectedNodeId === firstTargetId
                ? nodeAccentFromData({
                    fullStep: firstStep,
                    hasOptions: !!firstStep?.options?.length,
                  })
                : '#1E293B',
          opacity: 1,
        },
        markerEnd: {
          ...defaultEdgeStyle.markerEnd,
          color:
            selectedNodeId === 'start'
              ? '#43B02A'
              : selectedNodeId === firstTargetId
                ? nodeAccentFromData({
                    fullStep: firstStep,
                    hasOptions: !!firstStep?.options?.length,
                  })
                : '#1E293B',
        },
      });
    }

    // 2. Add Step Nodes
    flow.steps.forEach((step) => {
      let icon = <MessageCircleIcon />;
      let typeLabel = 'Interactive Message';
      const hasOptions =
        !step.is_datetime_step &&
        !step.is_quotation_step &&
        !isGoogleSheetStep(step) &&
        !isWindexStep(step) &&
        step.options &&
        step.options.length > 0;

      if (step.is_quotation_step) {
        icon = <ClipboardIcon />;
        typeLabel = 'Quotation Step';
      } else if (isWindexStep(step)) {
        icon = <ClipboardIcon color="#0F766E" />;
        typeLabel = 'Windex';
      } else if (isGoogleSheetStep(step)) {
        icon = <ClipboardIcon color="#1D4ED8" />;
        typeLabel = 'Google Sheet';
      } else if (step.is_datetime_step) {
        icon = <CalendarIcon />;
        typeLabel = 'Date & Time';
      } else if (step.is_confirmation_step) {
        icon = <CheckSquareIcon />;
        typeLabel = 'Confirmation';
      } else if (hasOptions) {
        icon = <ListIcon />;
        typeLabel = 'List Options (' + step.options.length + ')';
      } else if (step.is_open_ended) {
        icon = <MessageCircleIcon />;
        typeLabel = 'Open Ended';
      }

      const nodeId = step.node_id || `step_${step.order}`;

      newNodes.push({
        id: nodeId,
        type: 'custom',
        data: {
          isStart: false,
          hasOptions: hasOptions,
          branchRoutes: isGoogleSheetStep(step)
            ? [
                {
                  id: SHEET_FOUND_HANDLE,
                  label: 'Found',
                  linked: sheetFoundNextOrder(step) != null,
                },
                {
                  id: SHEET_NOT_FOUND_HANDLE,
                  label: 'Not Found',
                  linked: sheetNotFoundNextOrder(step) != null,
                },
              ]
            : isWindexStep(step)
              ? [
                  {
                    id: WINDEX_FOUND_HANDLE,
                    label: 'Found',
                    linked: step.next_step_order != null,
                  },
                  {
                    id: WINDEX_NO_RESULT_HANDLE,
                    label: 'No result',
                    linked: windexNoResultNextOrder(step) != null,
                  },
                ]
              : [],
          stepType: typeLabel,
          label: stripMarkdown(step.question_text) || `Step ${step.order}`,
          icon: icon,
          fullStep: step,
          selected:
            selectedNodeId === nodeId ||
            selectedNodeId === `step_${step.order}`,
          hasIncoming: targetedOrders.has(step.order),
          hasOutgoing: isGoogleSheetStep(step)
            ? sheetFoundNextOrder(step) != null ||
              sheetNotFoundNextOrder(step) != null
            : isWindexStep(step)
              ? step.next_step_order != null ||
                windexNoResultNextOrder(step) != null
              : hasOptions
                ? step.options.some((opt) => !!opt.next_step_order)
                : !!step.next_step_order,
        },
        position: { x: 0, y: 0 },
      });

      // Render Edges
      if (hasOptions) {
        const maxOptLen = (step.options || []).reduce(
          (m, o) => Math.max(m, (o.text || '').length),
          0,
        );
        const optionsList = step.options || [];
        optionsList.forEach((opt, idx) => {
          if (opt.next_step_order) {
            const targetStep = flow.steps.find(
              (s) => s.order === opt.next_step_order,
            );
            const targetNodeId =
              targetStep?.node_id || `step_${opt.next_step_order}`;
            newEdges.push({
              id: `e_${step.order}_opt${opt.number}_to_${opt.next_step_order}`,
              source: nodeId,
              sourceHandle: `opt_${opt.number}`,
              target: targetNodeId,
              targetHandle: 'main_in',
              ...defaultEdgeStyle,
              type: 'optionEdge',
              label: opt.text || `Option ${opt.number}`,
              data: {
                maxOptionLength: maxOptLen,
                optIndex: idx,
                totalOptions: optionsList.length,
              },
              labelBgPadding: [10, 5],
              labelBgBorderRadius: 6,
              labelBgStyle: {
                fill: '#FFFFFF',
                fillOpacity: 1,
                stroke: '#50AB3D',
                strokeWidth: 1.5,
              },
              labelStyle: {
                fill: '#1F2241',
                fontWeight: 600,
                fontSize: 11,
              },
            });
          }
        });
      } else if (isGoogleSheetStep(step)) {
        const sheetRoutes = [
          {
            handle: SHEET_FOUND_HANDLE,
            order: sheetFoundNextOrder(step),
            label: 'Found',
          },
          {
            handle: SHEET_NOT_FOUND_HANDLE,
            order: sheetNotFoundNextOrder(step),
            label: 'Not Found',
          },
        ];
        sheetRoutes.forEach((route) => {
          if (route.order == null) return;
          const targetStep = flow.steps.find((s) => s.order === route.order);
          const targetNodeId = targetStep?.node_id || `step_${route.order}`;
          newEdges.push({
            id: `e_${step.order}_${route.handle}_to_${route.order}`,
            source: nodeId,
            sourceHandle: route.handle,
            target: targetNodeId,
            targetHandle: 'main_in',
            ...defaultEdgeStyle,
            type: 'optionEdge',
            label: route.label,
          });
        });
      } else if (isWindexStep(step)) {
        [
          {
            handle: WINDEX_FOUND_HANDLE,
            order: step.next_step_order,
            label: 'Found',
          },
          {
            handle: WINDEX_NO_RESULT_HANDLE,
            order: windexNoResultNextOrder(step),
            label: 'No result',
          },
        ].forEach((route) => {
          if (route.order == null) return;
          const targetStep = flow.steps.find(
            (s) => s.order === Number(route.order),
          );
          const targetNodeId = targetStep?.node_id || `step_${route.order}`;
          newEdges.push({
            id: `e_${step.order}_${route.handle}_to_${route.order}`,
            source: nodeId,
            sourceHandle: route.handle,
            target: targetNodeId,
            targetHandle: 'main_in',
            ...defaultEdgeStyle,
            type: 'optionEdge',
            label: route.label,
          });
        });
      } else {
        // Standard Next Step
        if (step.next_step_order) {
          const targetStep = flow.steps.find(
            (s) => s.order === step.next_step_order,
          );
          const targetNodeId =
            targetStep?.node_id || `step_${step.next_step_order}`;
          newEdges.push({
            id: `e_${step.order}_main_to_${step.next_step_order}`,
            source: nodeId,
            sourceHandle: `main_out`,
            target: targetNodeId,
            targetHandle: 'main_in',
            ...defaultEdgeStyle,
            type: 'optionEdge',
          });
        }
      }
    });

    // Merge old positions or explicit positions if they exist
    setNodes((oldNodes) => {
      oldNodes.forEach((node) => {
        const pos = node.position;
        if (!pos) return;
        const parked =
          (pos.x === 0 && pos.y === 0) || (pos.x === 100 && pos.y === 100);
        if (parked) return;
        explicitNodePositionsRef.current.set(node.id, pos);
        const order = node.data?.fullStep?.order;
        if (order != null) {
          explicitNodePositionsRef.current.set(`order:${order}`, pos);
        }
      });
      const mappedNodes = newNodes.map((nn) => {
        const order = nn.data?.fullStep?.order;
        const explicitPos =
          explicitNodePositionsRef.current.get(nn.id) ||
          (order != null
            ? explicitNodePositionsRef.current.get(`order:${order}`)
            : null);
        if (explicitPos) {
          return { ...nn, position: explicitPos };
        }
        const existing = oldNodes.find(
          (on) =>
            on.id === nn.id ||
            (order != null && on.data?.fullStep?.order === order),
        );
        if (existing?.position) {
          return { ...nn, position: existing.position };
        }
        return {
          ...nn,
          position: {
            x: 100,
            y: 100,
          },
        };
      });

      // Initial layout on first render ONLY if no explicit positions exist
      if (!initialized.current) {
        initialized.current = true;
        if (explicitNodePositionsRef.current.size === 0) {
          const laidOut = DagreLayout(mappedNodes, newEdges, 'LR').nodes;
          laidOut.forEach((node) => {
            if (!node.position) return;
            explicitNodePositionsRef.current.set(node.id, node.position);
            const order = node.data?.fullStep?.order;
            if (order != null) {
              explicitNodePositionsRef.current.set(
                `order:${order}`,
                node.position,
              );
            }
          });
          return laidOut;
        }
      }
      return mappedNodes;
    });

    setEdges(newEdges);
  }, [flow, selectedNodeId]);

  useEffect(() => {
    syncGraphWithFlow();
  }, [syncGraphWithFlow]);

  // Panel open/close changes canvas padding on xl — remeasure React Flow.
  useEffect(() => {
    const instance = reactFlowInstanceRef.current;
    if (!instance) return undefined;
    const frame = requestAnimationFrame(() => {
      if (typeof instance.resize === 'function') {
        instance.resize();
      } else {
        window.dispatchEvent(new Event('resize'));
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedNodeId]);

  useEffect(() => {
    setLeadOpen(false);
    setOptionLeadKey(null);
    setSetupKind(null);
  }, [selectedNodeId]);

  // Selecting Quotation / DateTime / Open Ended mounts fields and can make
  // the browser scroll a parent (focus / overflow-anchor) — panel appears to
  // jump up and clip the header. Pin ancestor scroll after layout settles.
  const editStepBehaviorKey = (() => {
    if (!selectedNodeId || selectedNodeId === 'start') return 'none';
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    const step = (flow.steps || []).find((s) => s.order === order);
    if (!step) return 'none';
    return [
      step.is_quotation_step ? 'q' : '',
      step.is_datetime_step ? 'd' : '',
      step.is_open_ended ? 'o' : '',
      step.is_confirmation_step ? 'c' : '',
      isGoogleSheetStep(step) ? 'g' : '',
      isWindexStep(step) ? 'w' : '',
    ].join('');
  })();

  useLayoutEffect(() => {
    const scrollport = editStepScrollRef.current;
    if (!scrollport || !selectedNodeId) return;

    let node = scrollport.parentElement;
    while (node && node !== document.documentElement) {
      if (node.scrollTop) node.scrollTop = 0;
      if (node.scrollLeft) node.scrollLeft = 0;
      node = node.parentElement;
    }
    if (typeof window !== 'undefined' && (window.scrollY || window.scrollX)) {
      window.scrollTo(0, 0);
    }
  }, [selectedNodeId, editStepBehaviorKey]);

  // Re-run Dagre against the current graph on demand — fixes nodes that
  // were manually dragged out of place or scattered (new nodes added after
  // the initial layout get a random position, see syncGraphWithFlow).
  const handleAutoLayout = useCallback(() => {
    const instance = reactFlowInstanceRef.current;
    if (!instance) return;

    const measuredById = new Map(
      instance.getNodes().map((n) => [n.id, n.measured]),
    );

    setNodes((currentNodes) => {
      const nodesCopy = currentNodes.map((n) => ({
        ...n,
        measured: measuredById.get(n.id),
      }));
      const laidOut = DagreLayout(nodesCopy, edges, 'LR').nodes;
      laidOut.forEach((node) => {
        if (!node.position) return;
        explicitNodePositionsRef.current.set(node.id, node.position);
        const order = node.data?.fullStep?.order;
        if (order != null) {
          explicitNodePositionsRef.current.set(`order:${order}`, node.position);
        }
      });
      return laidOut;
    });
    requestAnimationFrame(() => {
      reactFlowInstanceRef.current?.fitView({
        padding: 0.16,
        maxZoom: 0.85,
        duration: 300,
      });
    });
  }, [edges]);

  autoLayoutRef.current = handleAutoLayout;

  // Imperatively fit the view once real nodes exist — the fitView prop
  // only applies at mount, when nodes are still empty, so it never
  // actually fits the populated layout on its own.
  useEffect(() => {
    tryFitView(nodes.length);
  }, [nodes, tryFitView]);

  const onNodesChangeHandler = useCallback(
    (changes) => {
      // 1. Prevent removing 'start' node
      const safeChanges = changes.filter(
        (c) => !(c.type === 'remove' && c.id === 'start'),
      );

      safeChanges.forEach((c) => {
        if (c.type === 'position' && c.position) {
          explicitNodePositionsRef.current.set(c.id, c.position);
        }
        if (
          c.type === 'dimensions' &&
          c.dimensions &&
          pendingImportLayoutRef.current
        ) {
          pendingImportMeasuredRef.current.add(c.id);
        }
      });

      const expectedImportIds = pendingImportNodeIdsRef.current;
      if (
        pendingImportLayoutRef.current &&
        expectedImportIds &&
        [...expectedImportIds].every((id) =>
          pendingImportMeasuredRef.current.has(id),
        )
      ) {
        pendingImportLayoutRef.current = false;
        queueMicrotask(() => {
          autoLayoutRef.current();
          setIsViewReady(true);
        });
      }

      if (!canEdit) {
        const allowed = safeChanges.filter(
          (c) => c.type === 'dimensions' || c.type === 'select',
        );
        if (allowed.length > 0) {
          setNodes((nds) => applyNodeChanges(allowed, nds));
        }
        return;
      }

      // Check if any nodes are being removed
      const removedChanges = safeChanges.filter((c) => c.type === 'remove');
      if (removedChanges.length > 0) {
        const removedNodeIds = new Set(removedChanges.map((c) => c.id));
        const removedOrders = new Set();

        removedNodeIds.forEach((id) => {
          explicitNodePositionsRef.current.delete(id);
          const match = id.match(/^step_(\d+)$/);
          if (match) {
            removedOrders.add(parseInt(match[1], 10));
          } else {
            const foundNode = nodes.find((n) => n.id === id);
            if (foundNode?.data?.fullStep?.order != null) {
              removedOrders.add(Number(foundNode.data.fullStep.order));
            }
          }
        });

        // 1. Remove from React Flow nodes
        setNodes((nds) => applyNodeChanges(safeChanges, nds));

        // 2. Remove all edges connected to removed nodes
        setEdges((eds) =>
          eds.filter(
            (e) =>
              !removedNodeIds.has(e.source) && !removedNodeIds.has(e.target),
          ),
        );

        // 3. Update flow.steps and clean broken references
        setFlow((prev) => {
          const filteredSteps = prev.steps.filter(
            (s) =>
              !removedOrders.has(s.order) && !removedNodeIds.has(s.node_id),
          );
          const stepsCleaned = filteredSteps.map((step) => {
            let updatedStep = { ...step };
            if (updatedStep.options) {
              updatedStep.options = updatedStep.options.map((opt) =>
                opt.next_step_order != null &&
                removedOrders.has(opt.next_step_order)
                  ? { ...opt, next_step_order: null }
                  : opt,
              );
            }
            if (
              updatedStep.next_step_order != null &&
              removedOrders.has(updatedStep.next_step_order)
            ) {
              updatedStep.next_step_order = null;
            }
            if (
              updatedStep.google_sheet_found_next_step_order != null &&
              removedOrders.has(
                Number(updatedStep.google_sheet_found_next_step_order),
              )
            ) {
              updatedStep.google_sheet_found_next_step_order = null;
            }
            if (
              updatedStep.google_sheet_not_found_next_step_order != null &&
              removedOrders.has(
                Number(updatedStep.google_sheet_not_found_next_step_order),
              )
            ) {
              updatedStep.google_sheet_no_match_next_step_order = null;
              updatedStep.google_sheet_not_found_next_step_order = null;
            }
            if (
              updatedStep.windex_no_result_next_step_order != null &&
              removedOrders.has(
                Number(updatedStep.windex_no_result_next_step_order),
              )
            ) {
              updatedStep.windex_no_result_next_step_order = null;
            }
            return updatedStep;
          });
          return { ...prev, steps: stepsCleaned };
        });

        // 4. Reset selected node if deleted node was selected
        if (removedNodeIds.has(selectedNodeId)) {
          setSelectedNodeId('start');
        }
        return;
      }

      setNodes((nds) => applyNodeChanges(safeChanges, nds));
    },
    [setNodes, setEdges, setFlow, canEdit, nodes, selectedNodeId],
  );

  // Edge deletions are handled explicitly via onEdgesChange below
  const onEdgesChangeHandler = useCallback(
    (changes) => {
      changes = changes.filter((c) => c.type !== 'select');
      if (!canEdit) return;
      setEdges((eds) => applyEdgeChanges(changes, eds));

      // Handle Edge Removal to sync back to Flow JSON
      changes.forEach((change) => {
        if (change.type === 'remove') {
          const edge = edges.find((e) => e.id === change.id);
          if (!edge) return;
          if (edge.source === 'start') return; // Start edge removal not allowed functionally here, but handled to avoid error

          const sourceOrder = parseInt(edge.source.replace('step_', ''), 10);

          if (
            edge.sourceHandle === WINDEX_FOUND_HANDLE ||
            edge.sourceHandle === WINDEX_NO_RESULT_HANDLE
          ) {
            setFlow((prev) => ({
              ...prev,
              steps: prev.steps.map((s) => {
                if (s.order !== sourceOrder) return s;
                if (edge.sourceHandle === WINDEX_FOUND_HANDLE) {
                  return { ...s, next_step_order: null };
                }
                return { ...s, windex_no_result_next_step_order: null };
              }),
            }));
          } else if (
            edge.sourceHandle === SHEET_FOUND_HANDLE ||
            edge.sourceHandle === SHEET_NOT_FOUND_HANDLE
          ) {
            setFlow((prev) => ({
              ...prev,
              steps: prev.steps.map((s) => {
                if (s.order !== sourceOrder) return s;
                if (edge.sourceHandle === SHEET_FOUND_HANDLE) {
                  return {
                    ...s,
                    google_sheet_found_next_step_order: null,
                    next_step_order: null,
                  };
                }
                return {
                  ...s,
                  google_sheet_no_match_next_step_order: null,
                  google_sheet_not_found_next_step_order: null,
                };
              }),
            }));
          } else if (
            edge.sourceHandle &&
            edge.sourceHandle.startsWith('opt_')
          ) {
            const optNumber = parseInt(
              edge.sourceHandle.replace('opt_', ''),
              10,
            );
            setFlow((prev) => ({
              ...prev,
              steps: prev.steps.map((s) => {
                if (s.order === sourceOrder) {
                  return {
                    ...s,
                    options: s.options.map((o) =>
                      o.number === optNumber
                        ? { ...o, next_step_order: null }
                        : o,
                    ),
                  };
                }
                return s;
              }),
            }));
          } else {
            setFlow((prev) => ({
              ...prev,
              steps: prev.steps.map((s) =>
                s.order === sourceOrder ? { ...s, next_step_order: null } : s,
              ),
            }));
          }
        }
      });
    },
    [setEdges, edges, setFlow, canEdit],
  );

  const onConnectHandler = useCallback(
    (params) => {
      if (!canEdit) return;
      // User creates a visual connection
      const { source, sourceHandle, target } = params;

      if (source === 'start') {
        // If start connects to something, maybe re-order it to 1?
        // Keeping it simple: We alert that start conventionally goes to order 1
        toast.error(
          'Trigger always starts at Step 1. To change sequence, edit Step orders.',
        );
        return;
      }

      const sourceOrder = parseInt(source.replace('step_', ''), 10);
      const targetOrder = parseInt(target.replace('step_', ''), 10);

      if (sourceOrder === targetOrder) {
        toast.error('Cannot connect a step to itself.');
        return;
      }

      setFlow((prev) => {
        const newSteps = [...prev.steps];
        const sIndex = newSteps.findIndex((s) => s.order === sourceOrder);
        if (sIndex < 0) return prev;

        if (
          sourceHandle === WINDEX_FOUND_HANDLE ||
          sourceHandle === WINDEX_NO_RESULT_HANDLE
        ) {
          const stepCopy = { ...newSteps[sIndex] };
          const otherOrder =
            sourceHandle === WINDEX_FOUND_HANDLE
              ? windexNoResultNextOrder(stepCopy)
              : stepCopy.next_step_order;
          if (otherOrder != null && Number(otherOrder) === targetOrder) {
            toast.error('Found and No result must connect to different steps.');
            return prev;
          }
          if (sourceHandle === WINDEX_FOUND_HANDLE) {
            stepCopy.next_step_order = targetOrder;
          } else {
            stepCopy.windex_no_result_next_step_order = targetOrder;
          }
          newSteps[sIndex] = stepCopy;
        } else if (
          sourceHandle === SHEET_FOUND_HANDLE ||
          sourceHandle === SHEET_NOT_FOUND_HANDLE
        ) {
          const stepCopy = { ...newSteps[sIndex] };
          const otherOrder =
            sourceHandle === SHEET_FOUND_HANDLE
              ? sheetNotFoundNextOrder(stepCopy)
              : sheetFoundNextOrder(stepCopy);
          if (otherOrder != null && otherOrder === targetOrder) {
            toast.error('Found and Not Found must connect to different steps.');
            return prev;
          }
          if (sourceHandle === SHEET_FOUND_HANDLE) {
            stepCopy.google_sheet_found_next_step_order = targetOrder;
            stepCopy.next_step_order = targetOrder;
          } else {
            stepCopy.google_sheet_no_match_next_step_order = targetOrder;
            stepCopy.google_sheet_not_found_next_step_order = targetOrder;
          }
          newSteps[sIndex] = stepCopy;
        } else if (sourceHandle && sourceHandle.startsWith('opt_')) {
          // It's a list option branch connection
          const optNumber = parseInt(sourceHandle.replace('opt_', ''), 10);
          const stepCopy = { ...newSteps[sIndex] };
          stepCopy.options = stepCopy.options.map((o) =>
            o.number === optNumber ? { ...o, next_step_order: targetOrder } : o,
          );
          newSteps[sIndex] = stepCopy;
        } else {
          // It's a standard main_out connection
          newSteps[sIndex] = {
            ...newSteps[sIndex],
            next_step_order: targetOrder,
          };
        }
        return { ...prev, steps: newSteps };
      });
    },
    [setFlow, canEdit],
  );

  const handleNodeClick = (_, node) => {
    setSelectedNodeId(node.id);
  };

  // --- Actions ---
  const handleAddNewStep = () => {
    if (!canEdit) return;
    const newOrder =
      flow.steps.length > 0
        ? Math.max(...flow.steps.map((s) => s.order)) + 1
        : 1;
    const newStep = {
      order: newOrder,
      question_text: 'New Question',
      options: [],
      tags: [],
      tag_ids: [],
      is_open_ended: false,
      variable_name: '',
      is_confirmation_step: false,
      is_datetime_step: false,
      is_quotation_step: false,
      is_google_sheet_step: false,
      is_windex_step: false,
      quotation_id: null,
      quotation_title: null,
      timezone: APPOINTMENT_TIMEZONE,
      working_hours_start: '09:00:00',
      working_hours_end: '17:00:00',
      slot_duration_minutes: 30,
      available_days_count: 7,
      next_step_order: null,
      media_type: 'text',
      media_url: '',
    };

    // Calculate smart position for new node
    const instance = reactFlowInstanceRef.current;
    const allRfNodes = instance ? instance.getNodes() : nodes;

    if (selectedNodeId && selectedNodeId !== 'start') {
      const selectedNode = allRfNodes.find((n) => n.id === selectedNodeId);
      if (selectedNode) {
        const sX = selectedNode.position?.x ?? 0;
        const sY = selectedNode.position?.y ?? 0;
        const sW = selectedNode.measured?.width || selectedNode.width || 320;
        explicitNodePositionsRef.current.set(`step_${newOrder}`, {
          x: sX + sW + 100,
          y: sY,
        });
      }
    } else if (allRfNodes.length > 0) {
      let maxX = 0;
      let refY = 100;
      allRfNodes.forEach((n) => {
        const x = (n.position?.x ?? 0) + (n.measured?.width || n.width || 320);
        if (x > maxX) {
          maxX = x;
          refY = n.position?.y ?? 100;
        }
      });
      explicitNodePositionsRef.current.set(`step_${newOrder}`, {
        x: maxX + 100,
        y: refY,
      });
    }

    setFlow((prev) => ({
      ...prev,
      steps: [...prev.steps, newStep],
    }));
    setSelectedNodeId(`step_${newOrder}`);
  };

  const handleRemoveStep = (orderToRemove) => {
    if (!canEdit) return;
    const nodeIdToRemove = `step_${orderToRemove}`;
    explicitNodePositionsRef.current.delete(nodeIdToRemove);
    explicitNodePositionsRef.current.delete(String(orderToRemove));

    // Remove from nodes state
    setNodes((nds) =>
      nds.filter(
        (n) =>
          n.id !== nodeIdToRemove && n.data?.fullStep?.order !== orderToRemove,
      ),
    );

    // Remove all edges connected to that node
    setEdges((eds) =>
      eds.filter(
        (e) =>
          e.source !== nodeIdToRemove &&
          e.target !== nodeIdToRemove &&
          !e.id.includes(`_${orderToRemove}_`),
      ),
    );

    setFlow((prev) => {
      const filteredSteps = prev.steps.filter((s) => s.order !== orderToRemove);
      const stepsCleaned = filteredSteps.map((step) => {
        let updatedStep = { ...step };
        if (updatedStep.options) {
          updatedStep.options = updatedStep.options.map((opt) =>
            opt.next_step_order === orderToRemove
              ? { ...opt, next_step_order: null }
              : opt,
          );
        }
        if (updatedStep.next_step_order === orderToRemove) {
          updatedStep.next_step_order = null;
        }
        if (
          Number(updatedStep.google_sheet_found_next_step_order) ===
          orderToRemove
        ) {
          updatedStep.google_sheet_found_next_step_order = null;
        }
        if (
          Number(updatedStep.google_sheet_not_found_next_step_order) ===
            orderToRemove ||
          Number(updatedStep.google_sheet_no_match_next_step_order) ===
            orderToRemove
        ) {
          updatedStep.google_sheet_no_match_next_step_order = null;
          updatedStep.google_sheet_not_found_next_step_order = null;
        }
        if (
          Number(updatedStep.windex_no_result_next_step_order) === orderToRemove
        ) {
          updatedStep.windex_no_result_next_step_order = null;
        }
        return updatedStep;
      });
      return { ...prev, steps: stepsCleaned };
    });
    setSelectedNodeId('start');
  };

  const updateFlowField = (field, value) => {
    if (!canEdit) return;
    setFlow((prev) =>
      field === 'keywords'
        ? { ...prev, keywords: value, keyword: value }
        : { ...prev, [field]: value },
    );
  };

  const updateSelectedStepField = (fieldOrObj, value) => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order === order) {
          if (typeof fieldOrObj === 'object' && fieldOrObj !== null) {
            return { ...step, ...fieldOrObj };
          }
          return { ...step, [fieldOrObj]: value };
        }
        return step;
      }),
    }));
  };

  const setDateTimeStepEnabled = (enabled) => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order !== order) return step;
        if (enabled) {
          return {
            ...step,
            is_datetime_step: true,
            is_open_ended: false,
            is_confirmation_step: false,
            is_quotation_step: false,
            is_google_sheet_step: false,
            ...windexFields({ is_windex_step: false }),
            quotation_id: null,
            quotation_title: null,
            options: [],
            timezone: APPOINTMENT_TIMEZONE,
            working_hours_start: step.working_hours_start || '09:00:00',
            working_hours_end: step.working_hours_end || '17:00:00',
            slot_duration_minutes: step.slot_duration_minutes || 30,
            available_days_count: step.available_days_count || 7,
          };
        }
        return { ...step, is_datetime_step: false };
      }),
    }));
  };

  const setQuotationStepEnabled = (enabled) => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order !== order) return step;
        if (enabled) {
          return {
            ...step,
            is_quotation_step: true,
            is_datetime_step: false,
            is_open_ended: false,
            is_confirmation_step: false,
            is_google_sheet_step: false,
            ...windexFields({ is_windex_step: false }),
            options: [],
            quotation_id: resolveQuotationId(step),
            question_text:
              step.question_text && step.question_text !== 'New Question'
                ? step.question_text
                : 'Please choose from our product catalog and reply with items & quantities (e.g. 1: 2, 2: 1)',
          };
        }
        return {
          ...step,
          is_quotation_step: false,
          quotation_id: null,
          quotation_title: null,
        };
      }),
    }));
  };

  const setGoogleSheetStepEnabled = (enabled) => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order !== order) return step;
        if (enabled) {
          return {
            ...step,
            is_google_sheet_step: true,
            ...windexFields({ is_windex_step: false }),
            is_open_ended: false,
            is_confirmation_step: false,
            is_datetime_step: false,
            is_quotation_step: false,
            quotation_id: null,
            quotation_title: null,
            options: [],
            google_sheet_lookup_source:
              step.google_sheet_lookup_source || 'message',
            google_sheet_name: step.google_sheet_name || 'Sheet1',
            google_sheet_no_match_message:
              step.google_sheet_no_match_message || SHEET_NO_MATCH_DEFAULT,
          };
        }
        return {
          ...step,
          ...googleSheetFields({ is_google_sheet_step: false }),
        };
      }),
    }));
  };

  const setWindexStepEnabled = (enabled) => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order !== order) return step;
        if (enabled) {
          return {
            ...step,
            is_windex_step: true,
            is_open_ended: false,
            is_confirmation_step: false,
            is_datetime_step: false,
            is_quotation_step: false,
            is_google_sheet_step: false,
            quotation_id: null,
            quotation_title: null,
            options: [],
            ...windexFields({ ...step, is_windex_step: true }),
          };
        }
        return {
          ...step,
          ...windexFields({ is_windex_step: false }),
        };
      }),
    }));
  };

  const addOptionToSelectedStep = () => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order === order) {
          if ((step.options?.length || 0) >= 10) {
            toast.error('Maximum 10 options allowed per step.');
            return step;
          }
          const nextOptNumber =
            step.options && step.options.length > 0
              ? Math.max(...step.options.map((o) => o.number)) + 1
              : 1;
          const newOptions = [
            ...(step.options || []),
            {
              number: nextOptNumber,
              text: `Option ${nextOptNumber}`,
              next_step_order: null,
              tags: [],
              tag_ids: [],
              stage_id: null,
            },
          ];
          return {
            ...step,
            options: newOptions,
            is_open_ended: false,
            is_confirmation_step: false,
            is_datetime_step: false,
            is_quotation_step: false,
            is_google_sheet_step: false,
            ...windexFields({ is_windex_step: false }),
            quotation_id: null,
            quotation_title: null,
            next_step_order: null,
          }; // clear main next step when using branches
        }
        return step;
      }),
    }));
  };

  const updateOptionInSelectedStep = (optionNumber, fieldOrObj, value) => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);

    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order === order) {
          const newOptions = step.options.map((opt) => {
            if (opt.number !== optionNumber) return opt;
            if (typeof fieldOrObj === 'object' && fieldOrObj !== null) {
              return { ...opt, ...fieldOrObj };
            }
            return { ...opt, [fieldOrObj]: value };
          });
          return { ...step, options: newOptions };
        }
        return step;
      }),
    }));
  };

  const removeOptionInSelectedStep = (optionNumber) => {
    if (!canEdit) return;
    if (!selectedNodeId || selectedNodeId === 'start') return;
    const order = parseInt(selectedNodeId.replace('step_', ''), 10);
    setFlow((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.order === order) {
          return {
            ...step,
            options: step.options.filter((o) => o.number !== optionNumber),
          };
        }
        return step;
      }),
    }));
  };

  // --- Derived states for the Property Panel ---
  const isStartSelected = selectedNodeId === 'start';
  const selectedStepData =
    selectedNodeId && !isStartSelected
      ? flow.steps.find(
          (s) => s.order === parseInt(selectedNodeId.replace('step_', ''), 10),
        )
      : null;
  const hasIncompleteQuotationStep = (flow.steps || []).some(
    (s) => s.is_quotation_step && !resolveQuotationId(s),
  );

  return (
    <div className="flow-builder h-full w-full min-h-0 flex flex-col overflow-hidden bg-[#FFFFFF] text-text-primary relative border border-border-default rounded-2xl">
      {/* Builder Header */}
      <div className="shrink-0 flex flex-col xl:flex-row xl:items-center justify-between p-2.5 sm:p-4 border-b border-border-default bg-white gap-2.5 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            onClick={handleBackClick}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-[8px] bg-white border border-[#E6E8EC] text-[#1E2340] hover:bg-[#F4F6F8] flex items-center justify-center transition-colors focus:outline-none shrink-0"
            aria-label="Back"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2
            className="text-sm sm:text-base md:text-h5 font-medium text-text-primary tracking-wide leading-tight truncate"
            title={flow.name || 'New Automation'}
          >
            {flow.name || 'New Automation'}
          </h2>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2.5 overflow-x-auto no-scrollbar py-0.5 max-w-full shrink-0">
          <button
            onClick={handleAutoLayout}
            className="w-7 h-7 sm:w-auto sm:h-9 sm:px-3 rounded-[8px] border border-[#E6E8EC] bg-white text-[#1E2340] hover:bg-[#F4F6F8] flex items-center justify-center sm:justify-start gap-1.5 text-xs sm:text-sm font-medium transition-colors focus:outline-none shrink-0"
            title="Auto Layout"
          >
            <svg
              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
            <span className="hidden xl:inline">Auto Layout</span>
          </button>
          <button
            onClick={() => setShowSimulator(!showSimulator)}
            className={`w-7 h-7 sm:w-9 sm:h-9 rounded-[8px] border flex items-center justify-center transition-colors focus:outline-none shrink-0 ${
              showSimulator
                ? 'bg-primary border-primary text-white hover:bg-primary-hover'
                : 'bg-white border-[#E6E8EC] text-[#1E2340] hover:bg-[#F4F6F8]'
            }`}
            title={showSimulator ? 'Hide Test' : 'Test Automation'}
            aria-label={showSimulator ? 'Hide Test' : 'Test Automation'}
          >
            <svg
              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="6" y="2" width="12" height="20" rx="4" />
              <line x1="10" y1="5" x2="14" y2="5" />
              <circle
                cx="12"
                cy="18"
                r="1.5"
                fill="currentColor"
                stroke="none"
              />
            </svg>
          </button>

          {canEdit && (
            <button
              type="button"
              onClick={handleImportClick}
              disabled={isSaving}
              className="w-7 h-7 sm:w-auto sm:h-9 sm:px-3 rounded-[8px] border border-[#E6E8EC] bg-white text-[#1E2340] hover:bg-[#F4F6F8] flex items-center justify-center sm:justify-start gap-1.5 text-xs sm:text-sm font-medium transition-colors focus:outline-none shrink-0 disabled:opacity-50"
              title="Import Flow (JSON)"
              aria-label="Import Flow (JSON)"
            >
              <svg
                className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span className="hidden xl:inline">Import</span>
            </button>
          )}

          {/* Direct Export Button */}
          <button
            type="button"
            onClick={handleExportClick}
            disabled={isExporting}
            className="w-7 h-7 sm:w-auto sm:h-9 sm:px-3 rounded-[8px] border border-[#E6E8EC] bg-white text-[#1E2340] hover:bg-[#F4F6F8] flex items-center justify-center sm:justify-start gap-1.5 text-xs sm:text-sm font-medium transition-colors focus:outline-none shrink-0 disabled:opacity-50"
            title="Export Flow (JSON)"
            aria-label="Export Flow (JSON)"
          >
            {isExporting ? (
              <span className="block w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            ) : (
              <UploadIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
            <span className="hidden xl:inline">Export</span>
          </button>
          {canSave && (
            <>
              <Button
                variant="secondary"
                onClick={() => persistFlow(true)}
                disabled={isSaving || hasIncompleteQuotationStep}
                className="!px-2 sm:!px-4 !py-1 sm:!py-2 !text-xs sm:!text-sm font-medium rounded-button !h-7 sm:!h-9 active:scale-95 whitespace-nowrap shrink-0"
              >
                <span className="hidden sm:inline">Save as Draft</span>
                <span className="sm:hidden">Draft</span>
              </Button>
              <Button
                variant="primary"
                onClick={() => persistFlow(false)}
                disabled={isSaving || hasIncompleteQuotationStep}
                className="!px-2.5 sm:!px-5 !py-1 sm:!py-2 !text-xs sm:!text-sm font-medium rounded-button shadow-lg !h-7 sm:!h-9 active:scale-95 whitespace-nowrap shrink-0"
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin h-3.5 w-3.5 sm:h-4 sm:w-4 text-text-primary shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Publish</span>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/*
        Workspace height = remaining chrome only (flex-1 min-h-0).
        Edit Step panel is ALWAYS position:absolute (all breakpoints), so its
        content height can never stretch the builder or page. On xl, canvas
        gets padding-right so the flow stays visible beside the panel.
      */}
      <div className="flex-1 min-h-0 w-full overflow-hidden relative">
        {selectedNodeId && (
          <div
            className="xl:hidden absolute inset-0 z-30 bg-black/40 transition-opacity"
            onClick={() => setSelectedNodeId(null)}
            aria-hidden="true"
          />
        )}

        {/* Canvas — full workspace; xl padding when panel open
            (panel 400 + right inset 16 + white divider 2 ≈ 418) */}
        <div
          className={[
            'flow-canvas h-full min-h-0 min-w-0 relative bg-[#FFFFFF] p-1.5 sm:p-3 xl:p-4 overflow-hidden transition-[padding] duration-200',
            selectedNodeId ? 'xl:pr-[420px]' : '',
          ].join(' ')}
        >
          <div className="relative w-full h-full min-h-0 min-w-0 rounded-xl sm:rounded-2xl bg-[#FAFBFF] overflow-hidden">
            {/* React Flow renders edges behind nodes by default, so the
                stub of each edge nearest a handle gets occluded by the
                node's own opaque background. Edges never route through a
                node's interior in this layout, so it's safe to draw them
                above nodes globally. */}
            <style>{`
              .react-flow__edges { z-index: 1 !important; }
              .react-flow__nodes { z-index: 5 !important; }
              .react-flow__node { z-index: 5 !important; }
              .react-flow__node.selected { z-index: 1000 !important; }
              .react-flow__edge-labels { z-index: 15 !important; }
              .react-flow__edge-path { vector-effect: non-scaling-stroke; }
              .react-flow__panel.bottom.left { margin: 0 !important; }
            `}</style>

            {/* Opaque cover — physically blocks any paint of the canvas
                underneath until fitView has run against fully-measured
                nodes. Independent of ReactFlow's own visibility class so it
                can't be defeated by any specificity/timing edge case there. */}
            {!isViewReady && (
              <div className="absolute inset-0 z-30 bg-[#FAFBFF]" />
            )}

            <div className="absolute bottom-3.5 right-3 sm:bottom-6 sm:right-6 z-10 flex items-center gap-2 sm:gap-3 pointer-events-auto">
              <button
                type="button"
                onClick={() => setShowSearchModal(true)}
                className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-input bg-white hover:bg-slate-50 text-[#1E2340] border border-border-default shadow-lg hover:scale-105 active:scale-95 transition-all group shrink-0"
                title="Search Steps & Nodes (Ctrl+K)"
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 text-text-secondary group-hover:text-[#1E2340] transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="7" strokeWidth="2" />
                  <path
                    strokeWidth="2"
                    strokeLinecap="round"
                    d="M20 20l-3.5-3.5"
                  />
                </svg>
              </button>

              {canEdit && (
                <Button
                  variant="primary"
                  onClick={handleAddNewStep}
                  className="flex items-center gap-1.5 sm:gap-2 !px-2.5 sm:!px-5 !py-1.5 sm:!py-3 rounded-input text-xs sm:text-sm font-medium shadow-lg !h-8 sm:!h-auto hover:scale-105 whitespace-nowrap shrink-0"
                  icon={<PlusIcon className="w-3.5 h-3.5 sm:w-5 sm:h-5" />}
                >
                  Add Node
                </Button>
              )}
            </div>

            <ReactFlow
              className={isViewReady ? 'visible' : 'invisible'}
              style={{ backgroundColor: '#FAFBFF' }}
              proOptions={{ hideAttribution: true }}
              defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
              onInit={(instance) => {
                reactFlowInstanceRef.current = instance;
                tryFitView(nodes.length);
              }}
              colorMode="light"
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={onNodesChangeHandler}
              onEdgesChange={onEdgesChangeHandler}
              onConnect={onConnectHandler}
              onNodeClick={handleNodeClick}
              onPaneClick={() => setSelectedNodeId(null)}
              fitViewOptions={{ padding: 0.16, maxZoom: 0.85 }}
              minZoom={0.02}
              maxZoom={2.5}
              connectionLineType={ConnectionLineType.SmoothStep}
              connectionLineStyle={{ stroke: '#1E293B', strokeWidth: 2 }}
              defaultEdgeOptions={{
                type: 'smoothstep',
                selectable: false,
                pathOptions: { borderRadius: 16, offset: 0 },
                style: { strokeWidth: 2, stroke: '#1E293B' },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: '#1E293B',
                  width: 18,
                  height: 18,
                },
              }}
              nodesDraggable={canEdit}
              nodesConnectable={canEdit}
              elementsSelectable={true}
              edgesReconnectable={canEdit}
            >
              <Controls
                position="bottom-left"
                orientation="horizontal"
                className="!m-0 !bottom-3.5 !left-3 sm:!bottom-6 sm:!left-6 !top-auto !bg-white !shadow-md !border !border-[#CBD5E1] !rounded-xl overflow-hidden !p-0.5 gap-0.5 [&>button]:!bg-transparent [&>button]:!border-0 [&>button]:!w-7 [&>button]:!h-7 sm:[&>button]:!w-9 sm:[&>button]:!h-9 [&>button]:!rounded-lg [&>button]:!text-[#1E2340] hover:[&>button]:!bg-[#F1F4F8] [&>button>svg]:!fill-current"
                style={{
                  '--xy-controls-button-background-color': 'transparent',
                  '--xy-controls-button-background-color-hover': '#F1F4F8',
                  '--xy-controls-button-color': '#1E2340',
                  '--xy-controls-button-color-hover': '#1E2340',
                  '--xy-controls-button-border-color': 'transparent',
                }}
              />
              <Background variant="dots" gap={18} size={1} color="#D5DAE3" />
            </ReactFlow>
          </div>
        </div>

        {/* Edit Step — inset matches canvas padding so header never clips
            under the builder card edge; body scrolls; focus cannot scroll parents */}
        {selectedNodeId && (
          <aside
            aria-label="Step configuration"
            className={[
              'flow-node-panel absolute z-40 flex flex-col overflow-hidden',
              /* Same inset as canvas padding — stable Y, no upward jump / clip */
              'top-1.5 right-1.5 bottom-1.5 left-1.5',
              'sm:top-3 sm:right-3 sm:bottom-3 sm:left-auto',
              'xl:top-4 xl:right-4 xl:bottom-4',
              'sm:w-[360px] xl:w-[400px]',
              /* Full-height white divider vs gray canvas (desktop side-by-side) */
              'xl:border-l-2 xl:border-solid xl:border-white xl:bg-white',
            ].join(' ')}
          >
            <div className="flex h-full max-h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-border-default bg-[#F5F5F5] shadow-2xl xl:shadow-none">
              {/* Fixed header — outside scroll */}
              <div className="z-10 flex shrink-0 items-center justify-between border-b border-border-default bg-white p-4 sm:p-5 rounded-t-2xl xl:bg-[#F5F5F5]">
                <h3 className="text-body-lg font-medium text-[#1E2340] truncate pr-2">
                  {isStartSelected
                    ? 'Edit Trigger'
                    : `Edit Step ${selectedStepData?.order}`}
                </h3>
                <div className="flex items-center gap-2 shrink-0">
                  {canEdit && !isStartSelected && selectedStepData && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          handleDuplicateStep(selectedStepData.order)
                        }
                        className="w-7 h-7 rounded-[6px] border border-[#E6E8EC] bg-white text-[#1E2340] flex items-center justify-center hover:bg-[#F4F6F8] transition-colors shrink-0"
                        title="Duplicate Step"
                        aria-label="Duplicate Step"
                      >
                        <DuplicateIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveStep(selectedStepData.order)}
                        className="w-7 h-7 rounded-[6px] border border-red-200 bg-white text-red-600 flex items-center justify-center hover:bg-red-50 transition-colors shrink-0"
                        title="Delete Step"
                        aria-label="Delete Step"
                      >
                        <img
                          src={trashIcon}
                          alt=""
                          className="w-4 h-4 object-contain shrink-0"
                        />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setSelectedNodeId(null)}
                    className="w-7 h-7 rounded-[6px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors shrink-0"
                    title="Close Sidebar"
                    aria-label="Close Sidebar"
                  >
                    <CloseIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/*
                h-0 + flex-1 = definite scrollport. overflow-anchor-none stops
                browser from scrolling ancestors when dynamic fields mount.
              */}
              <div
                ref={editStepScrollRef}
                className="h-0 min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain custom-scrollbar [overflow-anchor:none]"
              >
                <div className="box-border w-full min-w-0 space-y-5 px-4 py-5 pb-8 sm:px-5 [overflow-anchor:none]">
                  {isStartSelected ? (
                    // --- TRIGGER EDITOR ---
                    <>
                      <div className="space-y-2 text-left">
                        <label className="block text-xs font-medium text-text-secondary">
                          Flow Name
                        </label>
                        <input
                          type="text"
                          value={flow.name}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateFlowField('name', e.target.value)
                          }
                          className="w-full bg-white text-text-primary border border-transparent rounded-input px-4 py-2.5 outline-none shadow-sm focus:border-primary focus:ring-[3px] focus:ring-primary/15 transition-all font-medium"
                          placeholder="e.g. Sales Inquiry"
                        />
                      </div>
                      <div className="space-y-2 text-left">
                        <label className="block text-xs font-medium text-text-secondary">
                          Welcome Message
                        </label>
                        <textarea
                          rows={4}
                          value={flow.welcome_message}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateFlowField('welcome_message', e.target.value)
                          }
                          className="w-full bg-white text-text-primary border border-transparent rounded-input px-4 py-3 outline-none shadow-sm focus:border-primary focus:ring-[3px] focus:ring-primary/15 transition-all resize-none leading-relaxed text-sm"
                          placeholder="Enter your welcome message... (e.g. Hi {name}, welcome!)"
                        />
                      </div>
                      <div className="space-y-2 text-left">
                        <label className="block text-xs font-medium text-text-secondary">
                          Completion Message / સમાપ્તિ સંદેશ
                        </label>
                        <textarea
                          rows={4}
                          value={flow.completion_message || ''}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateFlowField(
                              'completion_message',
                              e.target.value,
                            )
                          }
                          className="w-full bg-white text-text-primary border border-transparent rounded-input px-4 py-3 outline-none shadow-sm focus:border-primary focus:ring-[3px] focus:ring-primary/15 transition-all resize-none leading-relaxed text-sm"
                          placeholder="Thank you! Your details have been saved."
                        />
                      </div>

                      {/* ── Trigger Keywords ─────────────────────────── */}
                      <div className="space-y-2 text-left pt-1 border-t border-border-default">
                        <div>
                          <label
                            htmlFor="flow-keywords-input"
                            className="block text-xs font-medium text-text-secondary mb-0.5"
                          >
                            Trigger Keywords
                          </label>
                          <p className="text-[11px] text-text-muted leading-snug mb-2">
                            When a customer sends a WhatsApp message containing
                            any of these keywords, this flow will start
                            automatically.
                          </p>
                        </div>

                        <KeywordTagInput
                          id="flow-keywords-input"
                          value={flow.keywords || ''}
                          disabled={!canEdit}
                          onChange={(val) => updateFlowField('keywords', val)}
                        />
                        <p className="text-[10px] text-text-muted">
                          Press{' '}
                          <kbd className="px-1 py-0.5 rounded bg-black/5 font-mono text-[10px]">
                            Enter
                          </kbd>{' '}
                          or{' '}
                          <kbd className="px-1 py-0.5 rounded bg-black/5 font-mono text-[10px]">
                            ,
                          </kbd>{' '}
                          after each keyword to add it.
                        </p>

                        {/* Status-aware banners */}
                        {flow.is_draft &&
                          parseKeywords(flow.keywords || '').length > 0 && (
                            <div className="flex items-start gap-2 rounded-[8px] bg-blue-50 border border-blue-200 px-3 py-2.5 text-[11px] text-blue-700 leading-snug">
                              <svg
                                className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-500"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span>
                                Draft flows cannot be triggered by keywords.
                                Publish and activate this flow to enable keyword
                                triggers.
                              </span>
                            </div>
                          )}

                        {!flow.is_draft &&
                          !flow.is_active &&
                          parseKeywords(flow.keywords || '').length > 0 && (
                            <div className="flex items-start gap-2 rounded-[8px] bg-amber-50 border border-amber-200 px-3 py-2.5 text-[11px] text-amber-700 leading-snug">
                              <svg
                                className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span>
                                This flow is currently inactive. Activate it to
                                allow keywords to trigger it.
                              </span>
                            </div>
                          )}
                      </div>
                      {/* ──────────────────────────────────────────────── */}

                      <div className="flex items-center gap-3 justify-start">
                        <input
                          type="checkbox"
                          id="is_default"
                          checked={flow.is_default}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateFlowField('is_default', e.target.checked)
                          }
                          className="w-4 h-4 text-primary bg-white border-border-default rounded focus:ring-primary focus:ring-offset-0"
                        />
                        <label
                          htmlFor="is_default"
                          className="text-body-sm font-medium text-text-primary cursor-pointer transition-colors"
                        >
                          Set as Default Flow (Triggered unconditionally)
                        </label>
                      </div>
                    </>
                  ) : selectedStepData ? (
                    // --- STEP EDITOR ---
                    <>
                      <div className="rounded-xl border border-border-default bg-gray-50 p-3 space-y-2 text-left">
                        <label className="block text-xs font-medium text-text-secondary">
                          Question Text
                        </label>
                        <textarea
                          rows={3}
                          value={selectedStepData.question_text || ''}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateSelectedStepField(
                              'question_text',
                              e.target.value,
                            )
                          }
                          className="w-full bg-white text-text-primary border border-transparent rounded-input px-4 py-3 outline-none shadow-sm focus:border-primary focus:ring-[3px] focus:ring-primary/15 transition-all resize-none leading-relaxed text-sm"
                          placeholder={
                            selectedStepData.is_quotation_step
                              ? 'Please choose from our product catalog and reply with items & quantities (e.g. 1: 2, 2: 1)'
                              : 'Type the message to send the user'
                          }
                        />
                      </div>

                      <div className="rounded-xl border border-border-default bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setLeadOpen((open) => !open)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                          aria-expanded={leadOpen}
                        >
                          <span className="text-xs font-medium text-text-secondary">
                            Lead Management
                          </span>
                          <svg
                            className={`h-4 w-4 text-text-muted transition-transform ${leadOpen ? 'rotate-180' : ''}`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        {leadOpen && (
                          <div className="space-y-4 border-t border-border-default bg-gray-50 px-3 py-3">
                            <InternalTagPicker
                              companyId={companyId}
                              instanceId={`step-${selectedStepData.order}`}
                              tags={selectedStepData.tags}
                              disabled={!canEdit}
                              onChange={(next) => updateSelectedStepField(next)}
                              helperText="Not shown on WhatsApp. Applied to the lead when this step is completed."
                            />
                            <PipelineStageSelect
                              stages={pipelineStages}
                              value={resolveStageId(selectedStepData)}
                              disabled={!canEdit}
                              onChange={(id, stage) =>
                                updateSelectedStepField({
                                  stage_id: id,
                                  stage: stage
                                    ? {
                                        id: stage.id,
                                        name: stage.name,
                                        color: stage.color,
                                        is_won: stage.is_won,
                                        is_lost: stage.is_lost,
                                      }
                                    : null,
                                })
                              }
                            />
                          </div>
                        )}
                      </div>

                      {/* Media Configuration */}
                      <div className="space-y-4 pt-4 border-t border-border-default text-left">
                        <label className="block text-xs font-medium text-text-secondary">
                          Media Attachments
                        </label>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-text-secondary mb-1">
                              Media Type
                            </label>
                            <CustomSelect
                              value={selectedStepData.media_type || 'text'}
                              disabled={!canEdit}
                              onChange={(val) =>
                                updateSelectedStepField('media_type', val)
                              }
                              options={[
                                { value: 'text', label: 'Text Only' },
                                { value: 'image', label: 'Image' },
                                { value: 'video', label: 'Video' },
                                { value: 'document', label: 'Document' },
                              ]}
                              hideCheckIcon
                              textSizeClass="text-body-sm"
                              buttonClassName={`w-full h-10 flex items-center justify-between rounded-input bg-white border border-transparent shadow-sm text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary px-3 transition-all ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                            />
                          </div>

                          {selectedStepData.media_type &&
                            selectedStepData.media_type !== 'text' && (
                              <div className="space-y-2">
                                {selectedStepData.media_type === 'link' ? (
                                  <>
                                    <label className="block text-xs font-medium text-text-secondary">
                                      Link URL
                                    </label>
                                    <input
                                      type="text"
                                      value={selectedStepData.media_url || ''}
                                      disabled={!canEdit}
                                      onChange={(e) =>
                                        updateSelectedStepField(
                                          'media_url',
                                          e.target.value,
                                        )
                                      }
                                      className="w-full bg-[#FFFFFF] text-text-primary border border-border-default rounded-xl px-4 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all text-sm"
                                      placeholder="https://example.com"
                                    />
                                  </>
                                ) : (
                                  <div
                                    className="relative border border-dashed border-border-default rounded-xl p-4 bg-white/[0.01] hover:bg-input-background hover:border-primary/30 transition-all flex flex-col items-center justify-center text-center cursor-pointer"
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const file = e.dataTransfer.files?.[0];
                                      if (!file) return;
                                      handleMediaUpload({
                                        target: { files: [file] },
                                      });
                                    }}
                                  >
                                    <input
                                      type="file"
                                      disabled={isUploadingFile || !canEdit}
                                      accept={
                                        selectedStepData.media_type === 'image'
                                          ? 'image/*'
                                          : selectedStepData.media_type ===
                                              'video'
                                            ? 'video/*'
                                            : '*/*'
                                      }
                                      onChange={handleMediaUpload}
                                      className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <svg
                                      className="w-5 h-5 text-text-secondary mb-1.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M12 4v16m8-8H4"
                                      />
                                    </svg>
                                    <span className="text-xs text-text-secondary font-medium">
                                      {isUploadingFile
                                        ? 'Uploading file...'
                                        : selectedStepData.media_url
                                          ? 'File attached. Drop another to replace.'
                                          : 'Drop a file here, or click to upload'}
                                    </span>
                                    <span className="text-[10px] text-text-muted mt-0.5">
                                      PNG, JPG, MP4, PDF formats
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Step Type toggles — auto height, no reserved space */}
                      <div className="space-y-3 pt-4 border-t border-border-default text-left min-w-0 w-full">
                        <label className="block text-xs font-medium text-text-secondary">
                          Step Type / Behavior
                        </label>
                        <div className="grid grid-cols-2 gap-2 text-left min-w-0">
                          <label
                            onMouseDown={(e) => e.preventDefault()}
                            className={`cursor-pointer flex items-center justify-between gap-3 p-3 rounded-[8px] border transition-colors min-w-0 ${
                              selectedStepData.is_open_ended
                                ? 'border-primary bg-primary/5'
                                : 'border-border-default hover:border-primary/40'
                            }`}
                          >
                            <span className="flex items-center gap-1.5 text-xs font-medium text-text-primary min-w-0">
                              <svg
                                className="h-3.5 w-3.5 shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M4 6h16M4 12h10M4 18h7" />
                              </svg>
                              Open Ended
                            </span>
                            <input
                              type="checkbox"
                              tabIndex={-1}
                              checked={!!selectedStepData.is_open_ended}
                              disabled={!canEdit}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  updateSelectedStepField({
                                    is_open_ended: true,
                                    is_confirmation_step: false,
                                    is_datetime_step: false,
                                    is_quotation_step: false,
                                    is_google_sheet_step: false,
                                    ...windexFields({ is_windex_step: false }),
                                    quotation_id: null,
                                    quotation_title: null,
                                    options: [],
                                    next_step_order: null,
                                  });
                                } else {
                                  updateSelectedStepField({
                                    is_open_ended: false,
                                    variable_name: '',
                                  });
                                }
                              }}
                              className="sr-only disabled:cursor-not-allowed"
                            />
                            <span
                              className={`hidden w-[18px] h-[18px] rounded-[4px] border-2 items-center justify-center shrink-0 transition-colors ${
                                selectedStepData.is_open_ended
                                  ? 'border-primary bg-primary'
                                  : 'border-border-default'
                              }`}
                            >
                              {selectedStepData.is_open_ended && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="2 6 5 9 10 3" />
                                </svg>
                              )}
                            </span>
                          </label>

                          <label
                            onMouseDown={(e) => e.preventDefault()}
                            className={`cursor-pointer flex items-center justify-between gap-3 p-3 rounded-[8px] border transition-colors min-w-0 ${
                              selectedStepData.is_datetime_step
                                ? 'border-primary bg-primary/5'
                                : 'border-border-default hover:border-primary/40'
                            }`}
                          >
                            <span className="flex items-center gap-1.5 text-xs font-medium text-text-primary min-w-0">
                              <svg
                                className="h-3.5 w-3.5 shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <rect
                                  x="3"
                                  y="4"
                                  width="18"
                                  height="18"
                                  rx="2"
                                />
                                <path d="M16 2v4M8 2v4M3 10h18" />
                              </svg>
                              Scheduling
                            </span>
                            <input
                              type="checkbox"
                              tabIndex={-1}
                              checked={!!selectedStepData.is_datetime_step}
                              disabled={!canEdit}
                              onChange={(e) =>
                                setDateTimeStepEnabled(e.target.checked)
                              }
                              className="sr-only disabled:cursor-not-allowed"
                            />
                            <span
                              className={`hidden w-[18px] h-[18px] rounded-[4px] border-2 items-center justify-center shrink-0 transition-colors ${
                                selectedStepData.is_datetime_step
                                  ? 'border-primary bg-primary'
                                  : 'border-border-default'
                              }`}
                            >
                              {selectedStepData.is_datetime_step && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="2 6 5 9 10 3" />
                                </svg>
                              )}
                            </span>
                          </label>

                          <label
                            onMouseDown={(e) => {
                              if (
                                !canEdit ||
                                (!canUseQuotations &&
                                  !selectedStepData.is_quotation_step)
                              ) {
                                return;
                              }
                              e.preventDefault();
                            }}
                            className={`flex items-center justify-between gap-3 p-3 rounded-[8px] border transition-colors min-w-0 ${
                              !canEdit ||
                              (!canUseQuotations &&
                                !selectedStepData.is_quotation_step)
                                ? 'cursor-not-allowed opacity-60'
                                : 'cursor-pointer'
                            } ${
                              selectedStepData.is_quotation_step
                                ? 'border-primary bg-primary/5'
                                : 'border-border-default hover:border-primary/40'
                            }`}
                          >
                            <span className="flex items-center gap-1.5 text-xs font-medium text-text-primary min-w-0">
                              <svg
                                className="h-3.5 w-3.5 shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M6 2h9l5 5v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                                <path d="M14 2v6h6" />
                              </svg>
                              Quotation
                              {!canUseQuotations && (
                                <span className="text-[9px] uppercase font-semibold tracking-wider text-amber-700 bg-amber-50 px-1 py-0.5 rounded border border-amber-200">
                                  Plan
                                </span>
                              )}
                            </span>
                            <input
                              type="checkbox"
                              tabIndex={-1}
                              checked={!!selectedStepData.is_quotation_step}
                              disabled={
                                !canEdit ||
                                (!canUseQuotations &&
                                  !selectedStepData.is_quotation_step)
                              }
                              onChange={(e) => {
                                if (
                                  !canUseQuotations &&
                                  !selectedStepData.is_quotation_step
                                ) {
                                  return;
                                }
                                setQuotationStepEnabled(e.target.checked);
                              }}
                              className="sr-only disabled:cursor-not-allowed"
                            />
                            <span
                              className={`hidden w-[18px] h-[18px] rounded-[4px] border-2 items-center justify-center shrink-0 transition-colors ${
                                selectedStepData.is_quotation_step
                                  ? 'border-primary bg-primary'
                                  : 'border-border-default'
                              }`}
                            >
                              {selectedStepData.is_quotation_step && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="2 6 5 9 10 3" />
                                </svg>
                              )}
                            </span>
                          </label>

                          <label
                            onMouseDown={(e) => e.preventDefault()}
                            className={`cursor-pointer flex items-center justify-between gap-3 p-3 rounded-[8px] border transition-colors min-w-0 ${
                              isGoogleSheetStep(selectedStepData)
                                ? 'border-primary bg-primary/5'
                                : 'border-border-default hover:border-primary/40'
                            }`}
                          >
                            <span className="flex items-center gap-1.5 text-xs font-medium text-text-primary min-w-0">
                              <svg
                                className="h-3.5 w-3.5 shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <ellipse cx="12" cy="5" rx="8" ry="3" />
                                <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
                                <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
                              </svg>
                              Sheets
                            </span>
                            <input
                              type="checkbox"
                              tabIndex={-1}
                              checked={isGoogleSheetStep(selectedStepData)}
                              disabled={!canEdit}
                              onChange={(e) =>
                                setGoogleSheetStepEnabled(e.target.checked)
                              }
                              className="sr-only disabled:cursor-not-allowed"
                            />
                            <span
                              className={`hidden w-[18px] h-[18px] rounded-[4px] border-2 items-center justify-center shrink-0 transition-colors ${
                                isGoogleSheetStep(selectedStepData)
                                  ? 'border-primary bg-primary'
                                  : 'border-border-default'
                              }`}
                            >
                              {isGoogleSheetStep(selectedStepData) && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="2 6 5 9 10 3" />
                                </svg>
                              )}
                            </span>
                          </label>

                          {(windexConnected ||
                            isWindexStep(selectedStepData)) && (
                            <label
                              onMouseDown={(e) => e.preventDefault()}
                              className={`cursor-pointer flex items-center justify-between gap-3 p-3 rounded-[8px] border transition-colors min-w-0 ${
                                isWindexStep(selectedStepData)
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border-default hover:border-primary/40'
                              }`}
                            >
                              <span className="flex items-center gap-1.5 text-xs font-medium text-text-primary min-w-0">
                                <img
                                  src={windexIcon}
                                  alt=""
                                  className="w-4 h-4 rounded-[2px] object-contain shrink-0"
                                />
                                Windex
                              </span>
                              <input
                                type="checkbox"
                                tabIndex={-1}
                                checked={isWindexStep(selectedStepData)}
                                disabled={!canEdit}
                                onChange={(e) =>
                                  setWindexStepEnabled(e.target.checked)
                                }
                                className="sr-only disabled:cursor-not-allowed"
                              />
                              <span
                                className={`hidden w-[18px] h-[18px] rounded-[4px] border-2 items-center justify-center shrink-0 transition-colors ${
                                  isWindexStep(selectedStepData)
                                    ? 'border-primary bg-primary'
                                    : 'border-border-default'
                                }`}
                              >
                                {isWindexStep(selectedStepData) && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <polyline points="2 6 5 9 10 3" />
                                  </svg>
                                )}
                              </span>
                            </label>
                          )}
                        </div>
                      </div>

                      {/* Behavior configs — mount only when selected; height = content */}
                      {selectedStepData.is_quotation_step && (
                        <div className="space-y-3 rounded-input bg-white p-4 shadow-sm min-w-0 w-full">
                          <QuotationPicker
                            companyId={companyId}
                            value={resolveQuotationId(selectedStepData)}
                            quotationTitle={
                              selectedStepData.quotation_title || null
                            }
                            disabled={!canEdit}
                            error={
                              (highlightQuotationErrors ||
                                hasIncompleteQuotationStep) &&
                              !resolveQuotationId(selectedStepData)
                            }
                            onChange={(id, catalog) => {
                              setHighlightQuotationErrors(false);
                              updateSelectedStepField({
                                quotation_id: id,
                                quotation: id,
                                quotation_title: catalog?.title || null,
                              });
                            }}
                          />
                          <div className="flex items-center justify-between gap-3 pt-3 border-t border-border-default">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-text-primary">
                                Show Prices in Catalog PDF
                              </p>
                              <p className="text-[11px] text-text-secondary mt-0.5">
                                Show product rates and prices in the catalog PDF
                                for this step (Yes / No).
                              </p>
                            </div>
                            <Switch
                              checked={
                                selectedStepData.quotation_show_price !== false
                              }
                              onChange={(checked) =>
                                updateSelectedStepField({
                                  quotation_show_price: checked,
                                })
                              }
                              disabled={!canEdit}
                              aria-label="Show prices in catalog PDF"
                            />
                          </div>
                        </div>
                      )}

                      {isWindexStep(selectedStepData) && (
                        <div className="rounded-xl border border-border-default bg-gray-50 p-3 space-y-2">
                          <p className="text-xs font-medium text-text-primary">
                            Windex fees
                          </p>
                          <p className="text-[11px] text-text-muted leading-snug">
                            {selectedStepData.windex_year
                              ? `Year ${selectedStepData.windex_year}`
                              : 'Year not set.'}
                          </p>
                          <button
                            type="button"
                            onClick={() => setSetupKind('windex')}
                            className="w-full h-9 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary-hover"
                          >
                            Configure Integration
                          </button>
                        </div>
                      )}
                      <Modal
                        isOpen={
                          setupKind === 'windex' &&
                          isWindexStep(selectedStepData)
                        }
                        onClose={() => setSetupKind(null)}
                        title="Windex fees"
                        description="WhatsApp sends this fee reply when the customer reaches this step."
                        size="2xl"
                        panelClassName="!bg-[#F5F5F5]"
                      >
                        <WindexStepFields
                          step={selectedStepData}
                          steps={flow.steps}
                          canEdit={canEdit}
                          showErrors={highlightQuotationErrors}
                          onChange={(patch) => {
                            setHighlightQuotationErrors(false);
                            updateSelectedStepField(patch);
                          }}
                        />
                      </Modal>

                      {isGoogleSheetStep(selectedStepData) && (
                        <div className="rounded-xl border border-border-default bg-gray-50 p-3 space-y-2">
                          <p className="text-xs font-medium text-text-primary">
                            Google Sheet lookup
                          </p>
                          <p className="text-[11px] text-text-muted leading-snug">
                            {selectedStepData.google_sheet_name
                              ? `Sheet: ${selectedStepData.google_sheet_name}`
                              : 'Lookup not configured yet.'}
                          </p>
                          <button
                            type="button"
                            onClick={() => setSetupKind('sheet')}
                            className="w-full h-9 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary-hover"
                          >
                            Configure Integration
                          </button>
                        </div>
                      )}
                      <Modal
                        isOpen={
                          setupKind === 'sheet' &&
                          isGoogleSheetStep(selectedStepData)
                        }
                        onClose={() => setSetupKind(null)}
                        title="Google Sheet lookup"
                        description="Choose the sheet, the answer to search, and what WhatsApp sends back."
                        size="2xl"
                        panelClassName="!bg-[#F5F5F5]"
                      >
                        <GoogleSheetStepFields
                          companyId={companyId}
                          step={selectedStepData}
                          steps={flow.steps}
                          canEdit={canEdit}
                          showErrors={highlightQuotationErrors}
                          onChange={(patch) => {
                            setHighlightQuotationErrors(false);
                            updateSelectedStepField(patch);
                          }}
                        />
                      </Modal>

                      {selectedStepData.is_datetime_step && (
                        <div className="rounded-xl border border-border-default bg-gray-50 p-3 space-y-2">
                          <p className="text-xs font-medium text-text-primary">
                            Date & time slots
                          </p>
                          <p className="text-[11px] text-text-muted leading-snug">
                            {toTimeInputValue(
                              selectedStepData.working_hours_start,
                              '09:00',
                            )}
                            {' – '}
                            {toTimeInputValue(
                              selectedStepData.working_hours_end,
                              '17:00',
                            )}
                            {' · '}
                            {selectedStepData.slot_duration_minutes || 30} min
                          </p>
                          <button
                            type="button"
                            onClick={() => setSetupKind('schedule')}
                            className="w-full h-9 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary-hover"
                          >
                            Setup Logic
                          </button>
                        </div>
                      )}
                      <Modal
                        isOpen={
                          setupKind === 'schedule' &&
                          !!selectedStepData.is_datetime_step
                        }
                        onClose={() => setSetupKind(null)}
                        title="Scheduling settings"
                        description="Set when customers can book. Times use Asia/Kolkata."
                        size="xl"
                        panelClassName="!bg-[#F5F5F5]"
                      >
                        <div className="space-y-3 min-w-0 w-full">
                          <div className="rounded-xl border border-border-default bg-white p-4 space-y-3">
                            <p className="text-sm font-medium text-text-primary">
                              Working hours
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5 text-left min-w-0">
                                <label className="block text-sm font-medium text-text-secondary">
                                  Opens at
                                </label>
                                <TimePickerInput
                                  value={toTimeInputValue(
                                    selectedStepData.working_hours_start,
                                    '09:00',
                                  )}
                                  disabled={!canEdit}
                                  onChange={(val) =>
                                    updateSelectedStepField(
                                      'working_hours_start',
                                      toApiTimeValue(val),
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-1.5 text-left min-w-0">
                                <label className="block text-sm font-medium text-text-secondary">
                                  Closes at
                                </label>
                                <TimePickerInput
                                  value={toTimeInputValue(
                                    selectedStepData.working_hours_end,
                                    '17:00',
                                  )}
                                  disabled={!canEdit}
                                  onChange={(val) =>
                                    updateSelectedStepField(
                                      'working_hours_end',
                                      toApiTimeValue(val),
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          <div className="rounded-xl border border-border-default bg-white p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-text-primary">
                                  Break
                                </p>
                                <p className="text-xs leading-snug text-text-muted">
                                  Slots inside this window stay hidden.
                                </p>
                              </div>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => {
                                  const breakOn = !!(
                                    selectedStepData.break_time_start &&
                                    selectedStepData.break_time_end
                                  );
                                  if (breakOn) {
                                    updateSelectedStepField({
                                      break_time_start: null,
                                      break_time_end: null,
                                    });
                                  } else {
                                    updateSelectedStepField({
                                      break_time_start: '13:00:00',
                                      break_time_end: '14:00:00',
                                    });
                                  }
                                }}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                  selectedStepData.break_time_start &&
                                  selectedStepData.break_time_end
                                    ? 'bg-primary'
                                    : 'bg-border-default'
                                } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    selectedStepData.break_time_start &&
                                    selectedStepData.break_time_end
                                      ? 'translate-x-4'
                                      : 'translate-x-0.5'
                                  }`}
                                />
                              </button>
                            </div>

                            {selectedStepData.break_time_start &&
                              selectedStepData.break_time_end && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div className="space-y-1.5 text-left min-w-0">
                                    <label className="block text-sm font-medium text-text-secondary">
                                      Break starts
                                    </label>
                                    <TimePickerInput
                                      value={toTimeInputValue(
                                        selectedStepData.break_time_start,
                                        '13:00',
                                      )}
                                      disabled={!canEdit}
                                      onChange={(val) =>
                                        updateSelectedStepField(
                                          'break_time_start',
                                          toApiTimeValue(val),
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="space-y-1.5 text-left min-w-0">
                                    <label className="block text-sm font-medium text-text-secondary">
                                      Break ends
                                    </label>
                                    <TimePickerInput
                                      value={toTimeInputValue(
                                        selectedStepData.break_time_end,
                                        '14:00',
                                      )}
                                      disabled={!canEdit}
                                      onChange={(val) =>
                                        updateSelectedStepField(
                                          'break_time_end',
                                          toApiTimeValue(val),
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                          </div>

                          <div className="rounded-xl border border-border-default bg-white p-4 space-y-3">
                            <p className="text-sm font-medium text-text-primary">
                              Booking window
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-1.5 text-left min-w-0">
                                <label className="block text-sm font-medium text-text-secondary">
                                  Days customers can pick
                                </label>
                                <CustomSelect
                                  value={String(
                                    selectedStepData.available_days_count || 7,
                                  )}
                                  disabled={!canEdit}
                                  onChange={(val) =>
                                    updateSelectedStepField(
                                      'available_days_count',
                                      parseInt(val, 10),
                                    )
                                  }
                                  options={AVAILABLE_DAYS_OPTIONS.map((n) => ({
                                    value: String(n),
                                    label: `${n} day${n === 1 ? '' : 's'}`,
                                  }))}
                                  hideCheckIcon
                                  align="center"
                                  textSizeClass="text-body-sm"
                                  buttonClassName={`w-full h-10 flex items-center justify-between rounded-[6px] bg-[#F5F5F5] border border-transparent text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary px-3 transition-all ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                                />
                              </div>
                              <div className="space-y-1.5 text-left min-w-0">
                                <label className="block text-sm font-medium text-text-secondary">
                                  Each slot lasts
                                </label>
                                <CustomSelect
                                  value={String(
                                    selectedStepData.slot_duration_minutes ||
                                      30,
                                  )}
                                  disabled={!canEdit}
                                  onChange={(val) =>
                                    updateSelectedStepField(
                                      'slot_duration_minutes',
                                      parseInt(val, 10),
                                    )
                                  }
                                  options={SLOT_DURATION_OPTIONS.map((n) => ({
                                    value: String(n),
                                    label: `${n} min`,
                                  }))}
                                  hideCheckIcon
                                  align="center"
                                  textSizeClass="text-body-sm"
                                  buttonClassName={`w-full h-10 flex items-center justify-between rounded-[6px] bg-[#F5F5F5] border border-transparent text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary px-3 transition-all ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Modal>

                      {/* Options — only for interactive (non-behavior) steps */}
                      {!selectedStepData.is_confirmation_step &&
                        !selectedStepData.is_open_ended &&
                        !selectedStepData.is_datetime_step &&
                        !selectedStepData.is_quotation_step &&
                        !isGoogleSheetStep(selectedStepData) &&
                        !isWindexStep(selectedStepData) && (
                          <div className="space-y-4 min-w-0 w-full">
                            <div className="flex justify-between items-center gap-2 min-w-0">
                              <label className="text-xs font-medium text-text-secondary">
                                Interactive Options
                              </label>
                              <span className="text-xxs bg-white shadow-sm px-2 py-0.5 rounded-pill text-text-muted font-medium shrink-0">
                                Connect visually or select below
                              </span>
                            </div>

                            <div className="space-y-3">
                              {selectedStepData.options?.map((opt, idx) => (
                                <div
                                  key={idx}
                                  className="bg-white rounded-xl border border-border-default p-3 space-y-3 flex flex-col text-left min-w-0"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="flex shrink-0 items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary text-xs font-medium">
                                      {opt.number}
                                    </span>
                                    <div className="relative flex-1 flex items-center min-w-0">
                                      <input
                                        type="text"
                                        value={opt.text}
                                        disabled={!canEdit}
                                        onChange={(e) =>
                                          updateOptionInSelectedStep(
                                            opt.number,
                                            'text',
                                            e.target.value,
                                          )
                                        }
                                        className="w-full bg-gray-50 text-body-sm font-medium text-text-primary border border-transparent rounded-lg px-2.5 py-1.5 pr-12 outline-none focus:border-primary transition-colors"
                                        placeholder="Option text"
                                      />
                                      <span
                                        className={`absolute right-2 text-[10px] select-none pointer-events-none font-medium ${
                                          (opt.text || '').length >
                                          OPTION_TEXT_MAX_LENGTH
                                            ? 'text-red-600 font-semibold'
                                            : (opt.text || '').length >=
                                                OPTION_TEXT_MAX_LENGTH
                                              ? 'text-amber-600 font-semibold'
                                              : 'text-text-muted'
                                        }`}
                                      >
                                        {(opt.text || '').length}/
                                        {OPTION_TEXT_MAX_LENGTH}
                                      </span>
                                    </div>
                                    {canEdit && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeOptionInSelectedStep(opt.number)
                                        }
                                        className="shrink-0 p-1 rounded-md hover:bg-red-50"
                                        title="Remove option"
                                        aria-label="Remove option"
                                      >
                                        <img
                                          src={trashIcon}
                                          alt=""
                                          className="w-4 h-4 object-contain"
                                        />
                                      </button>
                                    )}
                                  </div>
                                  <div className="rounded-lg border border-border-default overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const key = `${selectedStepData.order}-${opt.number}`;
                                        setOptionLeadKey((current) =>
                                          current === key ? null : key,
                                        );
                                      }}
                                      className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left"
                                      aria-expanded={
                                        optionLeadKey ===
                                        `${selectedStepData.order}-${opt.number}`
                                      }
                                    >
                                      <span className="text-xs font-medium text-text-secondary">
                                        Lead Management
                                      </span>
                                      <svg
                                        className={`h-4 w-4 text-text-muted transition-transform ${
                                          optionLeadKey ===
                                          `${selectedStepData.order}-${opt.number}`
                                            ? 'rotate-180'
                                            : ''
                                        }`}
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </button>
                                    {optionLeadKey ===
                                      `${selectedStepData.order}-${opt.number}` && (
                                      <div className="space-y-3 border-t border-border-default bg-gray-50 px-3 py-3">
                                        <InternalTagPicker
                                          compact
                                          companyId={companyId}
                                          instanceId={`step-${selectedStepData.order}-opt-${opt.number}`}
                                          tags={opt.tags}
                                          disabled={!canEdit}
                                          onChange={(next) =>
                                            updateOptionInSelectedStep(
                                              opt.number,
                                              next,
                                            )
                                          }
                                          helperText="Hidden on WhatsApp. Added to the lead when this option is chosen."
                                        />
                                        <PipelineStageSelect
                                          compact
                                          stages={pipelineStages}
                                          value={resolveStageId(opt)}
                                          disabled={!canEdit}
                                          helperText="This stage overrides the step stage. None keeps the step stage."
                                          onChange={(id, stage) =>
                                            updateOptionInSelectedStep(
                                              opt.number,
                                              {
                                                stage_id: id,
                                                stage: stage
                                                  ? {
                                                      id: stage.id,
                                                      name: stage.name,
                                                      color: stage.color,
                                                      is_won: stage.is_won,
                                                      is_lost: stage.is_lost,
                                                    }
                                                  : null,
                                              },
                                            )
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs text-text-secondary whitespace-nowrap shrink-0">
                                      Jumps to:
                                    </span>
                                    <CustomSelect
                                      className="flex-1 min-w-0"
                                      value={opt.next_step_order || ''}
                                      disabled={!canEdit}
                                      onChange={(val) => {
                                        const parsedVal =
                                          val === '' ? null : parseInt(val, 10);
                                        updateOptionInSelectedStep(
                                          opt.number,
                                          'next_step_order',
                                          parsedVal,
                                        );
                                      }}
                                      options={[
                                        {
                                          value: '',
                                          label: 'Unconnected (Ends Flow)',
                                        },
                                        ...flow.steps
                                          .filter(
                                            (s) =>
                                              s.order !==
                                              selectedStepData.order,
                                          )
                                          .map((s) => ({
                                            value: String(s.order),
                                            label: `Step ${s.order}: ${stripMarkdown(s.question_text) || 'Untitled'}`,
                                          })),
                                      ]}
                                      hideCheckIcon
                                      textSizeClass="text-xs"
                                      buttonClassName={`w-full h-9 flex items-center justify-between gap-2 rounded-[6px] bg-[#F5F5F5] border border-transparent text-text-primary text-xs font-sf-pro-display focus:outline-none focus:border-primary px-3 transition-all ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    />
                                  </div>
                                </div>
                              ))}
                              {(!selectedStepData.options ||
                                selectedStepData.options.length === 0) && (
                                <div className="text-caption text-center p-4 bg-white text-text-muted border-2 border-dashed border-border-default rounded-input">
                                  No options added. It acts as an open-ended
                                  message.
                                </div>
                              )}
                            </div>

                            {canEdit &&
                              (selectedStepData.options?.length || 0) < 10 && (
                                <button
                                  onClick={addOptionToSelectedStep}
                                  className="w-full flex items-center justify-center gap-2 py-2 border-2 border-dashed border-primary/40 text-caption font-medium text-primary rounded-input hover:border-primary hover:bg-primary/5 transition-colors"
                                >
                                  Add Option <PlusIcon className="w-4 h-4" />
                                </button>
                              )}
                          </div>
                        )}
                    </>
                  ) : (
                    <div className="text-center text-gray-500 text-sm mt-10">
                      Step not found.
                    </div>
                  )}
                </div>
              </div>
              {selectedStepData &&
                !isStartSelected &&
                (selectedStepData.is_open_ended ||
                  selectedStepData.is_datetime_step ||
                  selectedStepData.is_quotation_step ||
                  isGoogleSheetStep(selectedStepData) ||
                  !selectedStepData.options ||
                  selectedStepData.options.length === 0) && (
                  <div className="shrink-0 border-t border-border-default bg-white px-4 py-3 sm:px-5 space-y-2 rounded-b-2xl">
                    <label className="block text-xs font-medium text-text-secondary text-left">
                      Next Action
                    </label>
                    <CustomSelect
                      value={selectedStepData.next_step_order || ''}
                      disabled={!canEdit}
                      onChange={(val) => {
                        const parsedVal = val === '' ? null : parseInt(val, 10);
                        updateSelectedStepField('next_step_order', parsedVal);
                      }}
                      options={[
                        { value: '', label: 'None (End Flow)' },
                        ...flow.steps
                          .filter((s) => s.order !== selectedStepData.order)
                          .map((s) => ({
                            value: String(s.order),
                            label: `Step ${s.order}: ${stripMarkdown(s.question_text) || 'Untitled'}`,
                          })),
                      ]}
                      hideCheckIcon
                      textSizeClass="text-body-sm"
                      className="w-full min-w-0"
                      buttonClassName={`w-full h-10 flex items-center justify-between gap-2 rounded-input bg-gray-50 border border-border-default text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary px-3 transition-all ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                  </div>
                )}
            </div>
          </aside>
        )}
      </div>

      <SideDrawer
        isOpen={showSimulator}
        onClose={() => setShowSimulator(false)}
        width="md"
        bodyClassName="flex items-center justify-center p-6 bg-[#F5F5F5]"
        header={
          <div className="flex items-center justify-between w-full">
            <h2 className="text-body-lg font-medium text-primary">
              Test Automation
            </h2>
            <button
              onClick={() => setShowSimulator(false)}
              className="w-7 h-7 rounded-[6px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors shrink-0"
              title="Close"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>
        }
      >
        <FlowSimulator flow={flow} />
      </SideDrawer>

      <ImportFlowModal
        isOpen={showImportModal}
        onClose={handleImportModalClose}
        onSubmit={importJsonToCanvas}
        isPending={false}
        serverError={importError}
        onClearError={() => setImportError('')}
        willReplaceCanvas={canvasHasNodesToReplace}
      />

      <SearchNodeModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        steps={flow.steps}
        onSelectStep={handleSelectStepFromSearch}
        selectedStepOrder={
          selectedNodeId && selectedNodeId.startsWith('step_')
            ? parseInt(selectedNodeId.replace('step_', ''), 10)
            : null
        }
      />

      <ConfirmDeleteModal
        isOpen={showUnsavedConfirm}
        icon="warning"
        title="Unsaved Changes"
        description="You have unsaved changes. If you leave now without saving, this data will be lost."
        confirmText="Leave Without Saving"
        onConfirm={() => {
          setShowUnsavedConfirm(false);
          confirmLeave();
        }}
        onCancel={() => {
          setShowUnsavedConfirm(false);
          cancelLeave();
        }}
        secondaryText={canSave ? 'Save as Draft' : undefined}
        onSecondary={
          canSave
            ? () => {
                const saved = persistFlow(true);
                if (saved) {
                  setShowUnsavedConfirm(false);
                  cancelLeave();
                }
              }
            : undefined
        }
        isSecondaryPending={isSaving}
      />
    </div>
  );
}
